import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, WS_BASE_URL, chatAPI } from '../services/api';
import { userLabel, userInitial } from '../utils/displayName';

const formatBufferTime = (minutes) => {
  const m = parseFloat(minutes);
  if (isNaN(m)) return '5 minutes';
  if (m === 0.5) return '30 seconds';
  if (m === 1) return '1 minute';
  if (m < 1) return `${Math.round(m * 60)} seconds`;
  if (m === Math.floor(m)) return `${m} minutes`;
  return `${Math.floor(m)} min ${Math.round((m % 1) * 60)} sec`;
};

const formatTotalMinutes = (minutes) => {
  const m = parseFloat(minutes);
  if (isNaN(m)) return '0 min';
  if (m === Math.floor(m)) return `${m} min`;
  return `${Math.floor(m)} min ${Math.round((m % 1) * 60)} sec`;
};

const detectMobileLayout = () => {
  if (typeof window === 'undefined') return false;
  const width = window.innerWidth || 0;
  const height = window.innerHeight || 0;
  const maxTouchPoints = typeof navigator !== 'undefined'
    ? (navigator.maxTouchPoints || navigator.msMaxTouchPoints || 0)
    : 0;
  const coarseMatch = typeof window.matchMedia === 'function'
    ? window.matchMedia('(pointer: coarse)').matches
    : false;
  return width <= 900 || height <= 600 || coarseMatch || maxTouchPoints > 1;
};

const PrivateChat = () => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatPartner, setChatPartner] = useState(null);
  const [myPref, setMyPref] = useState(null);
  const [partnerPref, setPartnerPref] = useState(null);
  const [prefLocked, setPrefLocked] = useState(true);
  const [showPrefPrompt, setShowPrefPrompt] = useState(false);
  const [modalPref, setModalPref] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [connected, setConnected] = useState(false);
  const [taskFeature, setTaskFeature] = useState(false);
  const [roomTask, setRoomTask] = useState(null);
  const [roomTopic, setRoomTopic] = useState(null);
  const [featureMode, setFeatureMode] = useState('topics');
  const [timerStartedAt, setTimerStartedAt] = useState(null);
  const [elapsed, setElapsed] = useState('00:00');
  // Hard 10-minute cap: locks input + prompts the Guesser's verdict when reached.
  const [timeUp, setTimeUp] = useState(false);
  const timeUpTriggeredRef = useRef(false);
  // Topic rotation states
  const [allTopics, setAllTopics] = useState([]);
  const [currentTopicIndex, setCurrentTopicIndex] = useState(0);
  const [showTopicSwitchModal, setShowTopicSwitchModal] = useState(false);
  // Cultural Turing Test: Guesser's final-verdict modal shown when ending the chat
  const [showVerdictModal, setShowVerdictModal] = useState(false);
  // Follow-up answers collected with the verdict.
  const [verdict, setVerdict] = useState(null);              // 'german' | 'not_german'
  const [confidence, setConfidence] = useState(null);        // 1..5
  const [aiSuspicion, setAiSuspicion] = useState(null);      // 'human' | 'ai'
  const [verdictReason, setVerdictReason] = useState('');    // free text
  const [endingChat, setEndingChat] = useState(false);
  // Welcome modal state
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  // Welcome modal is a 3-step guided read: each step must be shown for a few
  // seconds before its advance button unlocks, so participants actually read.
  const WELCOME_TOTAL_STEPS = 3;
  const WELCOME_STEP_SECONDS = 4;
  const [welcomeStep, setWelcomeStep] = useState(1);
  const [welcomeStepSecondsLeft, setWelcomeStepSecondsLeft] = useState(WELCOME_STEP_SECONDS);
  const welcomeModalShownRef = useRef(false);
  const welcomeStorageKeyRef = useRef('');
  const modalShowingRef = useRef(false);
  const initialMessageCountRef = useRef(0);
  const initialMessagesCapturedRef = useRef(false);
  const mobileHeaderRef = useRef(null);
  const mobileInputRef = useRef(null);
  const [mobileHeaderHeight, setMobileHeaderHeight] = useState(86);
  const [mobileInputHeight, setMobileInputHeight] = useState(78);
  const [keyboardOffset, setKeyboardOffset] = useState(0);
  const [viewportOffsetTop, setViewportOffsetTop] = useState(0);
  const layoutViewportHeightRef = useRef(window.innerHeight);
  const [isPartnerTyping, setIsPartnerTyping] = useState(false);
  const partnerTypingTimeoutRef = useRef(null);
  const stopTypingBroadcastTimeoutRef = useRef(null);
  const lastTypingBroadcastRef = useRef(false);
  // Mobile responsiveness
  const [isMobile, setIsMobile] = useState(() => detectMobileLayout());
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const { user, logout, isWsOpen, joinRoom, sendTypingStatus, appSettings } = useAuth();
  // Hard cap on chat length (minutes). Configurable via the `chat.max_minutes`
  // setting; defaults to 10. At the cap the input locks and the Guesser is asked
  // for their verdict (so the round is still scored).
  const maxChatMinutes = (() => {
    const m = parseFloat(appSettings?.['chat.max_minutes'] ?? '10');
    return Number.isFinite(m) && m > 0 ? m : 10;
  })();
  const maxChatSeconds = Math.round(maxChatMinutes * 60);
  const roleInstructions = user?.user_type ? (appSettings?.[`instructions.${user.user_type}`] || '') : '';
  const roleLabel = user?.user_type === 'guesser' ? '🕵️ You are the Guesser'
    : user?.user_type === 'pretender' ? '🎭 You are the Player' : '';
  // Render admin-editable instruction text, turning **...** segments into bold.
  const renderRoleInstructions = (text) => {
    if (!text) return null;
    return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => (
      part.startsWith('**') && part.endsWith('**')
        ? <strong key={i}>{part.slice(2, -2)}</strong>
        : <React.Fragment key={i}>{part}</React.Fragment>
    ));
  };

  // --- Cultural Turing Test bonus reward (admin-tunable via app settings) ---
  const readSettingInt = (key, fallback) => {
    const n = parseInt(appSettings?.[key], 10);
    return Number.isFinite(n) ? n : fallback;
  };
  const bonusGuesserStart = readSettingInt('bonus.guesser.start', 30);      // cents
  const bonusGuesserPerQuestion = readSettingInt('bonus.guesser.per_question', 2); // cents/question
  const bonusPretenderFlat = readSettingInt('bonus.pretender.flat', 10);    // cents
  // Live running bonus for the Guesser: their own message count = questions asked.
  const myQuestionCount = messages.reduce((n, m) => {
    const sid = (m?.sender && m.sender.id) ?? m?.sender_id;
    return sid === user?.id ? n + 1 : n;
  }, 0);
  const liveGuesserBonus = Math.max(0, bonusGuesserStart - bonusGuesserPerQuestion * myQuestionCount);

  // Second instruction card: the bonus reward, shown right after the role card.
  const renderBonusCard = (modal) => {
    if (user?.user_type !== 'guesser' && user?.user_type !== 'pretender') return null;
    const isGuesser = user?.user_type === 'guesser';
    const wrap = modal
      ? {
          background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
          border: '2px solid #10b981',
          borderRadius: isMobile ? 14 : 18,
          padding: isMobile ? 18 : 24,
          marginBottom: isMobile ? 20 : 28,
        }
      : { ...styles.taskCard, marginBottom: 12, border: '2px solid #10b981', background: '#ecfdf5' };
    const titleStyle = modal
      ? { fontSize: isMobile ? 17 : 20, fontWeight: 700, color: '#065f46', margin: 0, marginBottom: 10 }
      : { fontWeight: 700, fontSize: 16, color: '#065f46' };
    const bodyStyle = modal
      ? { fontSize: isMobile ? 13 : 15, color: '#374151', lineHeight: 1.6 }
      : { marginTop: 8, fontSize: 13, color: '#374151', lineHeight: 1.5 };
    const noteStyle = {
      marginTop: 12, padding: '12px 14px', borderRadius: 10,
      background: 'rgba(245, 158, 11, 0.15)', border: '2px solid rgba(245,158,11,0.45)',
      fontSize: modal ? (isMobile ? 14 : 16) : 14, color: '#92400e', lineHeight: 1.55,
    };
    const instructionStyle = {
      marginTop: 12, padding: '10px 12px', borderRadius: 10,
      background: 'rgba(37, 99, 235, 0.10)', border: '2px solid rgba(37,99,235,0.35)',
      fontSize: modal ? (isMobile ? 14 : 16) : 14, color: '#1e40af', lineHeight: 1.55,
    };
    return (
      <div style={wrap}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={titleStyle}>💰 Bonus Reward</div>
        </div>
        {isGuesser ? (
          <div style={bodyStyle}>
            <div style={{ marginTop: modal ? 6 : 0 }}>
              You can earn a <strong>bonus of up to {bonusGuesserStart} cents</strong> based on how
              quickly you identify the other person's culture.
            </div>
            <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
              <li>You <strong>start with {bonusGuesserStart} cents</strong>.</li>
              <li>Each question you ask costs <strong>{bonusGuesserPerQuestion} cents</strong> — the faster you guess, the more you keep!</li>
              <li><strong>But be careful:</strong> if your final guess is <strong>wrong</strong>, you lose your <strong>entire</strong> bonus.</li>
            </ul>
            <div style={noteStyle}>
              ⚠️ <strong>Factual questions may not be helpful:</strong> They may use any tool — AI, Google,
              anything — to <strong>get information from</strong>. Keep this in mind when asking your questions!
            </div>
            <div style={instructionStyle}>
              👉 <strong>You start the chat.</strong>
            </div>
          </div>
        ) : (
          <div style={bodyStyle}>
            <div style={{ marginTop: modal ? 6 : 0 }}>
              You can earn a <strong>bonus of {bonusPretenderFlat} cents</strong> in this round.
            </div>
            <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
              <li>If you <strong>convince the other person that you are German</strong>, you earn <strong>{bonusPretenderFlat} cents</strong>.</li>
              <li>If they don't believe you, you earn nothing.</li>
            </ul>
            <div style={noteStyle}>
              ⚠️ <strong>Good to know:</strong> You may use any tool — AI, Google, anything — to
              <strong> get information from</strong>, but you still have to <strong>write your own
              messages yourself</strong>.
            </div>
            <div style={instructionStyle}>
              👉 <strong>Wait for the guesser to write the first message.</strong>
            </div>
          </div>
        )}
      </div>
    );
  };
  const navigate = useNavigate();
  const { roomId } = useParams();
  const emitTypingState = useCallback((shouldType) => {
    if (!sendTypingStatus || !roomId) return;
    const normalized = !!shouldType;
    if (lastTypingBroadcastRef.current === normalized) return;
    sendTypingStatus(roomId, normalized);
    lastTypingBroadcastRef.current = normalized;
  }, [roomId, sendTypingStatus]);
  
  useEffect(() => {
    if (!roomId || typeof window === 'undefined') return;
    const key = `room_${roomId}_welcome_seen`;
    welcomeStorageKeyRef.current = key;
    try {
      if (sessionStorage.getItem(key) === '1') {
        welcomeModalShownRef.current = true;
      }
    } catch (_) {}
    const taskKey = `room_${roomId}_task_intro_seen`;
    taskIntroStorageKeyRef.current = taskKey;
    try {
      if (sessionStorage.getItem(taskKey) === '1') {
        taskIntroShownRef.current = true;
      }
    } catch (_) {}
  }, [roomId]);

  const markWelcomeAsShown = () => {
    welcomeModalShownRef.current = true;
    if (welcomeStorageKeyRef.current && typeof window !== 'undefined') {
      try {
        sessionStorage.setItem(welcomeStorageKeyRef.current, '1');
      } catch (_) {}
    }
  };

  // Always restart the guided read from step 1 when the welcome modal opens.
  useEffect(() => {
    if (showWelcomeModal) setWelcomeStep(1);
  }, [showWelcomeModal]);

  // Per-step read gate: the advance button stays locked until this hits 0.
  useEffect(() => {
    if (!showWelcomeModal) return undefined;
    setWelcomeStepSecondsLeft(WELCOME_STEP_SECONDS);
    const id = setInterval(() => {
      setWelcomeStepSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [showWelcomeModal, welcomeStep]);

  const [topicTimerMinutes, setTopicTimerMinutes] = useState(2);
  const lastTopicIndexRef = useRef(-1);
  const topicSwitchAutoDismissRef = useRef(null);
  const bufferModalAutoDismissRef = useRef(null);
  const bufferEndTriggeredRef = useRef(false);
  const bufferModalDismissedRef = useRef(false);
  const messageInputRef = useRef(null);
  // Buffer time (after all topics or task)
  const [showBufferModal, setShowBufferModal] = useState(false);
  const [bufferModalType, setBufferModalType] = useState('topic'); // 'topic' | 'task'
  const [bufferTimerMinutes, setBufferTimerMinutes] = useState(5);
  const [taskBufferTimerMinutes, setTaskBufferTimerMinutes] = useState(5);
  const [bufferRemainingSeconds, setBufferRemainingSeconds] = useState(null); // kept for compatibility, not displayed
  const bufferShowingRef = useRef(false);
  // Task mode: intro modal, task timer, task time-up
  const [showTaskIntroModal, setShowTaskIntroModal] = useState(false);
  const taskIntroShownRef = useRef(false);
  const taskIntroStorageKeyRef = useRef('');
  const [taskTimerMinutes, setTaskTimerMinutes] = useState(5);

  useEffect(() => {
    modalShowingRef.current = showTopicSwitchModal;
  }, [showTopicSwitchModal]);

  // Topic switch modal: 30-second auto-dismiss when shown
  useEffect(() => {
    if (!showTopicSwitchModal) return;
    if (topicSwitchAutoDismissRef.current) {
      clearTimeout(topicSwitchAutoDismissRef.current);
    }
    topicSwitchAutoDismissRef.current = setTimeout(() => {
      topicSwitchAutoDismissRef.current = null;
      modalShowingRef.current = false;
      setShowTopicSwitchModal(false);
    }, 30000);
    return () => {
      if (topicSwitchAutoDismissRef.current) {
        clearTimeout(topicSwitchAutoDismissRef.current);
        topicSwitchAutoDismissRef.current = null;
      }
    };
  }, [showTopicSwitchModal]);

  // Reset topic boundary tracking when room or topics change
  useEffect(() => {
    lastTopicIndexRef.current = -1;
  }, [roomId, allTopics]);
  useEffect(() => {
    bufferShowingRef.current = showBufferModal;
  }, [showBufferModal]);

  const topicsCount = allTopics.length;
  const messageCount = messages.length;
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const wsRef = useRef(null);

  const scrollToBottom = (behavior = 'smooth') => {
    const container = messagesContainerRef.current;
    if (container) {
      const remaining = Math.max(
        0,
        container.scrollHeight - container.clientHeight - container.scrollTop
      );
      const resolvedBehavior = behavior === 'smooth'
        ? (remaining < 800 ? 'smooth' : 'auto')
        : 'auto';
      if (typeof container.scrollTo === 'function') {
        try {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: resolvedBehavior
          });
        } catch (_) {
          container.scrollTop = container.scrollHeight;
        }
      } else {
        container.scrollTop = container.scrollHeight;
      }
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  const clearTimerPersistence = (rid) => {
    try {
      localStorage.removeItem(`room_${rid}_timer_started_at`);
      localStorage.removeItem(`room_${rid}_server_now_at_fetch`);
      localStorage.removeItem(`room_${rid}_client_now_at_fetch`);
      localStorage.removeItem(`room_${rid}_timer_started_at_ms`);
      localStorage.removeItem(`room_${rid}_server_now_at_fetch_ms`);
      localStorage.removeItem(`room_${rid}_client_start_ms`);
      localStorage.removeItem(`room_${rid}_server_offset_ms`);
    } catch (_) {}
  };

  const clearAllTimerData = () => {
    try {
      // Clear all room timer data from localStorage
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.includes('room_') && (
          key.includes('_timer_') || 
        key.includes('_client_start_') ||
        key.includes('_server_now_') ||
        key.includes('_server_offset_')
        )) {
          localStorage.removeItem(key);
        }
      });
    } catch (_) {}
  };

  // Topic switch modal: dismiss only (timer keeps running; topic is derived from elapsed)
  const handleNextTopic = () => {
    modalShowingRef.current = false;
    setShowTopicSwitchModal(false);
    if (topicSwitchAutoDismissRef.current) {
      clearTimeout(topicSwitchAutoDismissRef.current);
      topicSwitchAutoDismissRef.current = null;
    }
  };

  const handleBufferDismiss = () => {
    bufferModalDismissedRef.current = true;
    bufferShowingRef.current = false;
    setShowBufferModal(false);
    if (bufferModalAutoDismissRef.current) {
      clearTimeout(bufferModalAutoDismissRef.current);
      bufferModalAutoDismissRef.current = null;
    }
    // Buffer countdown and auto-end are derived from elapsed (synced for both users)
  };

  const triggerBufferEnd = useCallback(async () => {
    if (bufferEndTriggeredRef.current) return;
    bufferEndTriggeredRef.current = true;
    const payload = {
      ended_after_buffer: true,
      topics_covered_count: featureMode === 'topics' ? allTopics.length : undefined
    };
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/chat/room/${roomId}/end`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      clearTimerPersistence(roomId);
      try { localStorage.setItem('show_study_completion_code', 'true'); } catch (_) {}
      navigate('/chat');
    } catch (e) {
      alert('Failed to end chat.');
    }
  }, [roomId, featureMode, allTopics.length, navigate]);

  // Task timer and buffer are now derived from elapsed time (same as topics)

  // End the chat. For a Guesser, `guess` carries their final verdict
  // ('german' | 'not_german'); the backend scores the round from it.
  const submitEndChat = useCallback(async (guess, extra = {}) => {
    if (endingChat) return;
    setEndingChat(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ended_after_buffer: false,
        topics_covered_count: featureMode === 'topics' ? currentTopicIndex + 1 : undefined,
        ...(guess ? { guess } : {}),
        ...(extra && extra.confidence != null ? { confidence: extra.confidence } : {}),
        ...(extra && extra.ai_suspicion ? { ai_suspicion: extra.ai_suspicion } : {}),
        ...(extra && extra.reason && extra.reason.trim() ? { reason: extra.reason.trim() } : {})
      };
      const endResp = await axios.post(`${API_BASE_URL}/chat/room/${roomId}/end`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      clearTimerPersistence(roomId);
      try {
        if (endResp?.data?.bonus) {
          localStorage.setItem('round_bonus_result', JSON.stringify(endResp.data.bonus));
        }
      } catch (_) {}
      try { localStorage.setItem('show_study_completion_code', 'true'); } catch (_) {}
      setShowVerdictModal(false);
      navigate('/chat');
    } catch (e) {
      alert('Failed to end chat.');
      setEndingChat(false);
    }
  }, [endingChat, featureMode, currentTopicIndex, roomId, navigate]);

  // "End Chat" button: Guessers must give a verdict first; others end directly.
  const handleEndChatClick = useCallback(() => {
    if (user?.user_type === 'guesser') {
      setShowVerdictModal(true);
    } else if (user?.user_type === 'pretender') {
      // Pretenders never finalize the round: ending it deletes the Guesser's
      // membership and would discard their (not-yet-submitted) verdict. The
      // Guesser ends the chat with their verdict; the Pretender just waits.
      return;
    } else {
      submitEndChat(null);
    }
  }, [user?.user_type, submitEndChat]);


  // Handle window resize / pointer changes for mobile detection
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    
    const handleResize = () => {
      setIsMobile(detectMobileLayout());
    };
    
    window.addEventListener('resize', handleResize);
    
    let coarseMql = null;
    if (typeof window.matchMedia === 'function') {
      coarseMql = window.matchMedia('(pointer: coarse)');
      if (coarseMql.addEventListener) {
        coarseMql.addEventListener('change', handleResize);
      } else if (coarseMql.addListener) {
        coarseMql.addListener(handleResize);
      }
    }
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (coarseMql) {
        if (coarseMql.removeEventListener) {
          coarseMql.removeEventListener('change', handleResize);
        } else if (coarseMql.removeListener) {
          coarseMql.removeListener(handleResize);
        }
      }
    };
  }, []);

  // Mobile keyboard handling - keep header/input glued while allowing refresh when keyboard closed
  useEffect(() => {
    if (!isMobile || typeof window === 'undefined') {
      setKeyboardOffset(0);
      setViewportOffsetTop(0);
      return;
    }
    
    const originalBodyStyles = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      width: document.body.style.width,
      height: document.body.style.height,
    };
    const originalHtmlOverflow = document.documentElement.style.overflow;
    let bodyLocked = false;
    
    const lockBody = () => {
      if (bodyLocked) return;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      document.documentElement.style.overflow = 'hidden';
      bodyLocked = true;
    };
    
    const unlockBody = () => {
      if (!bodyLocked) return;
      document.body.style.overflow = originalBodyStyles.overflow;
      document.body.style.position = originalBodyStyles.position;
      document.body.style.width = originalBodyStyles.width;
      document.body.style.height = originalBodyStyles.height;
      document.documentElement.style.overflow = originalHtmlOverflow;
      bodyLocked = false;
    };
    
    const handleViewportChange = () => {
      if (!window.visualViewport) {
        setKeyboardOffset(0);
        setViewportOffsetTop(0);
        return;
      }
      
      const vv = window.visualViewport;
      const offsetTop = Math.max(0, Math.round(vv.offsetTop || 0));
      setViewportOffsetTop(offsetTop);
      const currentVisibleHeight = vv.height + offsetTop;
      const baseHeight = layoutViewportHeightRef.current || window.innerHeight;
      const rawOffset = Math.max(0, Math.round(baseHeight - currentVisibleHeight));
      const keyboardVisible = rawOffset > 80;
      
      if (keyboardVisible) {
        lockBody();
        setKeyboardOffset(rawOffset);
        window.scrollTo(0, 0);
      } else {
        unlockBody();
        setKeyboardOffset(0);
        layoutViewportHeightRef.current = Math.round(currentVisibleHeight);
      }
    };
    
    const handleOrientationChange = () => {
      layoutViewportHeightRef.current = window.innerHeight;
      setKeyboardOffset(0);
      setViewportOffsetTop(0);
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(handleViewportChange);
      } else {
        setTimeout(handleViewportChange, 0);
      }
    };
    
    handleViewportChange();
    
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportChange);
      window.visualViewport.addEventListener('scroll', handleViewportChange);
    } else {
      window.addEventListener('resize', handleViewportChange);
    }
    window.addEventListener('orientationchange', handleOrientationChange);
    
    return () => {
      unlockBody();
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportChange);
        window.visualViewport.removeEventListener('scroll', handleViewportChange);
      } else {
        window.removeEventListener('resize', handleViewportChange);
      }
      window.removeEventListener('orientationchange', handleOrientationChange);
      setKeyboardOffset(0);
      setViewportOffsetTop(0);
    };
  }, [isMobile]);

  // Track mobile header/input heights to size scroll region precisely
  useEffect(() => {
    if (!isMobile) {
      setMobileHeaderHeight(0);
      setMobileInputHeight(0);
      return;
    }

    const measure = () => {
      if (mobileHeaderRef.current) {
        setMobileHeaderHeight(mobileHeaderRef.current.offsetHeight || 0);
      }
      if (mobileInputRef.current) {
        setMobileInputHeight(mobileInputRef.current.offsetHeight || 0);
      }
    };

    measure();

    let resizeObserver;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(measure);
      if (mobileHeaderRef.current) resizeObserver.observe(mobileHeaderRef.current);
      if (mobileInputRef.current) resizeObserver.observe(mobileInputRef.current);
    } else {
      window.addEventListener('resize', measure);
    }
    window.addEventListener('orientationchange', measure);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', measure);
      }
      window.removeEventListener('orientationchange', measure);
    };
  }, [isMobile]);

  useEffect(() => {
    scrollToBottom('smooth');
  }, [messages]);

  useEffect(() => {
    if (isMobile) {
      scrollToBottom('auto');
    }
  }, [isMobile, keyboardOffset, mobileInputHeight, mobileHeaderHeight, viewportOffsetTop]);

  useEffect(() => {
    return () => {
      if (partnerTypingTimeoutRef.current) {
        clearTimeout(partnerTypingTimeoutRef.current);
      }
      if (stopTypingBroadcastTimeoutRef.current) {
        clearTimeout(stopTypingBroadcastTimeoutRef.current);
      }
      emitTypingState(false);
      lastTypingBroadcastRef.current = false;
    };
  }, [emitTypingState]);


  // Mark this room as active while this page is open
  useEffect(() => {
    if (roomId) {
      localStorage.setItem('activeRoomId', String(roomId));
      // Clear unread badge for this room immediately upon viewing
      try {
        const uid = (user && user.id) || (JSON.parse(localStorage.getItem('user') || '{}')?.id);
        if (uid) {
          const key = `unreadByRoom_${uid}`;
          const raw = localStorage.getItem(key);
          const map = raw ? (JSON.parse(raw) || {}) : {};
          if (map[String(roomId)]) {
            delete map[String(roomId)];
            localStorage.setItem(key, JSON.stringify(map));
            try {
              const total = Object.values(map).reduce((a,b)=>a+(b||0),0);
              const ev = new CustomEvent('unread_updated', { detail: { roomId: String(roomId), total } });
              window.dispatchEvent(ev);
            } catch(_) {}
          }
        }
      } catch (e) {
        // ignore storage errors
      }
    }
    return () => {
      const active = localStorage.getItem('activeRoomId');
      if (active === String(roomId)) {
        localStorage.removeItem('activeRoomId');
      }
    };
  }, [roomId, user]);

  useEffect(() => {
    let messagePollingInterval;
    
    if (roomId) {
      // Reset timer display state but don't clear persistence yet
      setElapsed('00:00');
      
      // Inform server that this user joined this room to receive timer_started if already running
      try { joinRoom && joinRoom(Number(roomId)); } catch (_) {}
      fetchMessages();
      
      // Poll for new messages every 5 seconds as backup to WebSocket
      messagePollingInterval = setInterval(() => {
        fetchMessages();
      }, 5000);
      
      // Load settings (feature flags)
      (async () => {
        try {
          const res = await axios.get(`${API_BASE_URL}/settings`);
          const enabled = String(res?.data?.settings?.['feature.tasks'] ?? 'true').toLowerCase() === 'true';
          setTaskFeature(enabled);
          const mode = String(res?.data?.settings?.['feature.mode'] ?? 'topics').toLowerCase();
          console.log('Feature mode loaded:', mode);
          setFeatureMode(mode);
          const timerMinutes = parseFloat(res?.data?.settings?.['topic.timer_minutes'] ?? '2');
          const finalMinutes = isNaN(timerMinutes) || timerMinutes <= 0 ? 2 : timerMinutes;
          console.log('Loaded topic timer setting:', { timerMinutes, finalMinutes });
          setTopicTimerMinutes(finalMinutes);
          const bufMin = parseFloat(res?.data?.settings?.['topic.buffer_minutes'] ?? '5');
          const bufClamped = isNaN(bufMin) ? 5 : Math.min(5, Math.max(0.5, bufMin));
          setBufferTimerMinutes(bufClamped);
          const taskBufMin = parseFloat(res?.data?.settings?.['task.buffer_minutes'] ?? '5');
          setTaskBufferTimerMinutes(isNaN(taskBufMin) ? 5 : Math.min(5, Math.max(0.5, taskBufMin)));
          const taskMin = parseInt(res?.data?.settings?.['task.timer_minutes'] ?? '5', 10);
          setTaskTimerMinutes(isNaN(taskMin) ? 5 : Math.min(60, Math.max(1, taskMin)));
          
          try {
            const token = localStorage.getItem('token');
            // Fetch task or topic based on mode
            if (mode === 'tasks') {
              const resp = await chatAPI.getRoomTask(roomId, { headers: { Authorization: `Bearer ${token}` } });
              const t = resp?.data?.task || null;
              setRoomTask(t);
              setRoomTopic(null);
              if (t && !taskIntroShownRef.current) {
                setShowTaskIntroModal(true);
              }
            } else if (mode === 'topics') {
              const resp = await axios.get(`${API_BASE_URL}/chat/room/${roomId}/topic`, { headers: { Authorization: `Bearer ${token}` } });
              const topics = resp?.data?.topics || [];
              const currentIndex = resp?.data?.current_topic_index || 0;
              setAllTopics(topics);
              setCurrentTopicIndex(currentIndex);
              setRoomTopic(topics[currentIndex] || null);
              setRoomTask(null);
              
              // Show welcome modal when topics are loaded (only once per session)
              if (topics.length > 0 && !welcomeModalShownRef.current) {
                markWelcomeAsShown();
                setShowWelcomeModal(true);
              }
            } else {
              // Cultural Turing Test (feature.mode = 'none'): no task/topic to fetch.
              // Show the welcome modal once so participants read their role instructions.
              setRoomTask(null);
              setRoomTopic(null);
              setAllTopics([]);
              if (!welcomeModalShownRef.current) {
                markWelcomeAsShown();
                setShowWelcomeModal(true);
              }
            }
          } catch (_) {}
          // Fetch timer snapshot to align elapsed for recipients who weren't in the room
          try {
            const token = localStorage.getItem('token');
            const tr = await chatAPI.getRoomTimer(roomId, { headers: { Authorization: `Bearer ${token}` } });
            const tsIso = tr?.data?.timer_started_at || null;
            const tsMs = tr?.data?.timer_started_at_ms || null;
            const serverNowMs = tr?.data?.server_now_ms || null;
            
            // Only set timer if it actually exists on server
            if (tsIso && tsMs && serverNowMs) {
              setTimerStartedAt(tsIso);
              try {
                const startMsNum = Number(tsMs);
                const serverNowMsNum = Number(serverNowMs);
                
                console.log('Loading timer:', { startMs: startMsNum, serverNowMs: serverNowMsNum, diff: serverNowMsNum - startMsNum });
                
                // Always update timer data when loading room
                localStorage.setItem(`room_${roomId}_timer_started_at`, tsIso);
                localStorage.setItem(`room_${roomId}_timer_started_at_ms`, String(startMsNum));
                localStorage.setItem(`room_${roomId}_server_now_at_fetch_ms`, String(serverNowMsNum));
                
                // Server-time sync: store offset so elapsed = server_now - start (avoids 1s drift between users)
                const serverOffsetMs = serverNowMsNum - Date.now();
                localStorage.setItem(`room_${roomId}_server_offset_ms`, String(serverOffsetMs));
                
                // Fallback client baseline for when server_offset is missing
                const elapsedOnServer = Math.max(0, serverNowMsNum - startMsNum);
                const clientBaseline = Date.now() - elapsedOnServer;
                localStorage.setItem(`room_${roomId}_client_start_ms`, String(clientBaseline));
                
                console.log('Timer baseline set on load:', { elapsedOnServer, clientBaseline, now: Date.now() });
              } catch (e) {
                console.error('Timer sync error:', e);
              }
            } else {
              // No timer on server, clear any stale local data
              setTimerStartedAt(null);
              clearTimerPersistence(roomId);
            }
          } catch (_) {}
        } catch (_) {}
      })();
      // Ensure partner name is shown even before first message
      setTimeout(() => {
        if (!chatPartner) {
          fetchRoomPartner();
        }
      }, 0);
    } else {
      setLoading(false);
    }
    
    return () => {
      if (messagePollingInterval) {
        clearInterval(messagePollingInterval);
      }
    };
  }, [roomId, user]);

  // Message polling removed - now handled in main useEffect above with 5 second interval

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/chat/room/${roomId}/messages`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setMessages(response.data);
      if (!initialMessagesCapturedRef.current) {
        initialMessagesCapturedRef.current = true;
        initialMessageCountRef.current = response.data.length;
      } else if (response.data.length < initialMessageCountRef.current) {
        initialMessageCountRef.current = response.data.length;
      }
      
      // Find chat partner (the other user in the room)
      if (response.data.length > 0) {
        const otherUser = response.data.find(msg => msg.sender.id !== user.id)?.sender;
        if (otherUser) {
          setChatPartner(otherUser);
        }
      } else {
        // No messages yet, try to derive partner from rooms list
        await fetchRoomPartner();
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      const status = error?.response?.status;
      if (status === 403 || status === 404) {
        navigate('/chat');
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomPartner = async () => {
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.get(`${API_BASE_URL}/chat/rooms`, { headers: { Authorization: `Bearer ${token}` } });
      const room = (resp.data || []).find(r => String(r.id) === String(roomId));
      if (room && Array.isArray(room.members)) {
        const partner = room.members.find(m => m.id && m.id !== user.id);
        if (partner) {
          setChatPartner(partner);
        }
      }
    } catch (e) {
      // ignore
    }
  };

  const fetchLanguagePreference = async () => {};

  const setLanguagePreference = async (preferred_language) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/chat/room/${roomId}/language-preference`,
        { preferred_language },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMyPref(response.data.preferred_language || null);
      setPartnerPref(response.data.partner_preferred_language || null);
      setPrefLocked(response.data.preference_locked);
      setShowPrefPrompt(false);
    } catch (e) {
      alert('Failed to set language preference. Make sure you choose one of your registered languages.');
    }
  };

  // Use global WebSocket events to update this view (avoid multiple sockets per user)
  useEffect(() => {
    const handler = (evt) => {
      const data = evt?.detail;
      if (!data) return;
      if (data.type === 'new_message' && Number(data?.data?.room_id) === Number(roomId)) {
        setMessages(prev => [...prev, data.data]);
        // Keep partner info fresh
        if (!chatPartner) fetchRoomPartner();
        // Stop broadcasting typing since we just sent a message
        emitTypingState(false);
      } else if (data.type === 'user_joined') {
        if (!chatPartner && data.user) setChatPartner(data.user);
      } else if (data.type === 'typing' && Number(data.room_id) === Number(roomId)) {
        const senderId = Number(data.user_id);
        if (senderId && senderId !== Number(user?.id)) {
          if (partnerTypingTimeoutRef.current) {
            clearTimeout(partnerTypingTimeoutRef.current);
          }
          if (data.is_typing) {
            setIsPartnerTyping(true);
            partnerTypingTimeoutRef.current = setTimeout(() => {
              setIsPartnerTyping(false);
            }, 4000);
          } else {
            setIsPartnerTyping(false);
          }
        }
      } else if (data.type === 'chat_ended' && Number(data.room_id) === Number(roomId)) {
        clearTimerPersistence(roomId);
        try {
          if (data.bonus) {
            localStorage.setItem('round_bonus_result', JSON.stringify(data.bonus));
          }
        } catch (_) {}
        try { localStorage.setItem('show_study_completion_code', 'true'); } catch (_) {}
        // Chat ended: return to the waiting screen (no questionnaire).
        navigate('/chat');
      } else if (data.type === 'task_assigned' && Number(data.room_id) === Number(roomId)) {
        if (taskFeature) setRoomTask(data.task || null);
      } else if (data.type === 'topic_assigned' && Number(data.room_id) === Number(roomId)) {
        setRoomTopic(data.topic || null);
      } else if (data.type === 'topics_assigned' && Number(data.room_id) === Number(roomId)) {
        // New event for multiple topics
        const topics = data.topics || [];
        console.log('Topics assigned via WebSocket:', { topicsCount: topics.length, topics });
        setAllTopics(topics);
        setCurrentTopicIndex(data.current_topic_index || 0);
        setRoomTopic(topics[0] || null);
      } else if (data.type === 'topic_timer_started' && Number(data.room_id) === Number(roomId)) {
        // Topic is derived from elapsed time - no separate timer to start
      } else if (data.type === 'topic_notification' && Number(data.room_id) === Number(roomId)) {
        // Each client self-detects topic boundaries from elapsed time - no sync needed
      } else if (data.type === 'buffer_started' && Number(data.room_id) === Number(roomId)) {
        if (bufferShowingRef.current || bufferModalDismissedRef.current) return;
        bufferShowingRef.current = true;
        setBufferModalType('topic');
        setShowBufferModal(true);
        setShowTopicSwitchModal(false);
        setShowWelcomeModal(false);
        if (bufferModalAutoDismissRef.current) clearTimeout(bufferModalAutoDismissRef.current);
        bufferModalAutoDismissRef.current = setTimeout(() => {
          bufferModalAutoDismissRef.current = null;
          bufferModalDismissedRef.current = true;
          bufferShowingRef.current = false;
          setShowBufferModal(false);
        }, 15000);
      } else if (data.type === 'task_buffer_started' && Number(data.room_id) === Number(roomId)) {
        if (bufferShowingRef.current || bufferModalDismissedRef.current) return;
        bufferShowingRef.current = true;
        setBufferModalType('task');
        setShowBufferModal(true);
        setShowTaskIntroModal(false);
        if (bufferModalAutoDismissRef.current) clearTimeout(bufferModalAutoDismissRef.current);
        bufferModalAutoDismissRef.current = setTimeout(() => {
          bufferModalAutoDismissRef.current = null;
          bufferModalDismissedRef.current = true;
          bufferShowingRef.current = false;
          setShowBufferModal(false);
        }, 15000);
      } else if (data.type === 'topic_advanced' && Number(data.room_id) === Number(roomId)) {
        // Topic is derived from elapsed time - no sync needed
      } else if (data.type === 'settings_updated') {
        // Admin changed settings/tasks/topics - refresh based on mode
        (async () => {
          try {
            const token = localStorage.getItem('token');
            const settingsRes = await axios.get(`${API_BASE_URL}/settings`);
            const mode = String(settingsRes?.data?.settings?.['feature.mode'] ?? 'topics').toLowerCase();
            setFeatureMode(mode);
            const timerMinutes = parseFloat(settingsRes?.data?.settings?.['topic.timer_minutes'] ?? '2');
            setTopicTimerMinutes(isNaN(timerMinutes) || timerMinutes <= 0 ? 2 : timerMinutes);
            const bufMin = parseFloat(settingsRes?.data?.settings?.['topic.buffer_minutes'] ?? '5');
            setBufferTimerMinutes(isNaN(bufMin) ? 5 : Math.min(5, Math.max(0.5, bufMin)));
            const taskBufMin = parseFloat(settingsRes?.data?.settings?.['task.buffer_minutes'] ?? '5');
            setTaskBufferTimerMinutes(isNaN(taskBufMin) ? 5 : Math.min(5, Math.max(0.5, taskBufMin)));
            const taskMin = parseInt(settingsRes?.data?.settings?.['task.timer_minutes'] ?? '5', 10);
            setTaskTimerMinutes(isNaN(taskMin) ? 5 : Math.min(60, Math.max(1, taskMin)));
            
            if (mode === 'tasks') {
              const resp = await chatAPI.getRoomTask(roomId, { headers: { Authorization: `Bearer ${token}` } });
              setRoomTask(resp?.data?.task || null);
              setRoomTopic(null);
              setAllTopics([]);
            } else if (mode === 'topics') {
              const resp = await axios.get(`${API_BASE_URL}/chat/room/${roomId}/topic`, { headers: { Authorization: `Bearer ${token}` } });
              const topics = resp?.data?.topics || [];
              const currentIndex = resp?.data?.current_topic_index || 0;
              setAllTopics(topics);
              setCurrentTopicIndex(currentIndex);
              setRoomTopic(topics[currentIndex] || null);
              setRoomTask(null);
            }
          } catch (_) {}
        })();
      } else if (data.type === 'timer_started' && Number(data.room_id) === Number(roomId)) {
        // Timer just started - synchronize with server time
        setTimerStartedAt(data.timer_started_at || null);
        
        try {
          const startMs = Number(data.timer_started_at_ms);
          const serverNowMs = Number(data.server_now_ms);
          
          console.log('Timer started event:', { startMs, serverNowMs, diff: serverNowMs - startMs });
          
          if (startMs && serverNowMs && !isNaN(startMs) && !isNaN(serverNowMs)) {
            localStorage.setItem(`room_${roomId}_timer_started_at`, data.timer_started_at);
            localStorage.setItem(`room_${roomId}_timer_started_at_ms`, String(startMs));
            localStorage.setItem(`room_${roomId}_server_now_at_fetch_ms`, String(serverNowMs));
            
            // Server-time sync: store offset so elapsed = server_now - start (avoids 1s drift between users)
            const serverOffsetMs = serverNowMs - Date.now();
            localStorage.setItem(`room_${roomId}_server_offset_ms`, String(serverOffsetMs));
            
            // Fallback client baseline
            const elapsedOnServer = Math.max(0, serverNowMs - startMs);
            const clientBaseline = Date.now() - elapsedOnServer;
            localStorage.setItem(`room_${roomId}_client_start_ms`, String(clientBaseline));
            
            console.log('Timer baseline set:', { elapsedOnServer, clientBaseline, now: Date.now() });
          }
        } catch (e) {
          console.error('Timer sync error:', e);
        }
      }
    };
    window.addEventListener('app_ws_event', handler);
    return () => window.removeEventListener('app_ws_event', handler);
  }, [roomId, chatPartner, taskFeature, featureMode, allTopics, currentTopicIndex, topicTimerMinutes, emitTypingState, user?.id]);

  // Reflect global ws state and compute elapsed timer
  useEffect(() => {
    setConnected(!!isWsOpen);
  }, [isWsOpen]);

  useEffect(() => {
    let id;
    const compute = () => {
      // If no timer started yet, show 00:00
      if (!timerStartedAt) {
        setElapsed('00:00');
        return;
      }
      
      // Use server-time sync when available (avoids ~1s drift between users)
      let elapsedMs = 0;
      try {
        const startMs = parseInt(localStorage.getItem(`room_${roomId}_timer_started_at_ms`) || '0', 10);
        const serverOffsetMs = parseInt(localStorage.getItem(`room_${roomId}_server_offset_ms`) || 'NaN', 10);
        if (startMs > 0 && !isNaN(serverOffsetMs)) {
          const serverNowMs = Date.now() + serverOffsetMs;
          elapsedMs = Math.max(0, serverNowMs - startMs);
        }
      } catch (_) {}
      
      // Fallback to client baseline if server offset missing
      if (elapsedMs <= 0) {
        let clientStart = 0;
        try {
          const storedStart = localStorage.getItem(`room_${roomId}_client_start_ms`);
          if (storedStart) clientStart = parseInt(storedStart, 10);
        } catch (_) {}
        if (!clientStart || isNaN(clientStart) || clientStart <= 0) {
          clientStart = Date.now();
          localStorage.setItem(`room_${roomId}_client_start_ms`, String(clientStart));
        }
        elapsedMs = Date.now() - clientStart;
      }
      const elapsedSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
      const mm = String(Math.floor(elapsedSeconds / 60)).padStart(2, '0');
      const ss = String(elapsedSeconds % 60).padStart(2, '0');
      
      // Debug: Log if timer seems incorrect
      if (elapsedSeconds > 3600) {
        console.warn('Timer appears incorrect:', { elapsedSeconds, now: Date.now(), elapsed: elapsedMs });
      }
      
      setElapsed(`${mm}:${ss}`);

      // Hard cap: the chat lasts at most `maxChatSeconds`. When reached, lock the
      // input for both roles. The Guesser is prompted for their final verdict (so
      // the round is still scored); the Pretender waits — the room ends when the
      // Guesser submits. Any other/unknown role just ends the chat.
      if (maxChatSeconds > 0 && elapsedSeconds >= maxChatSeconds && !timeUpTriggeredRef.current) {
        timeUpTriggeredRef.current = true;
        setTimeUp(true);
        if (user?.user_type === 'guesser') {
          setShowVerdictModal(true);
        } else if (user?.user_type !== 'pretender') {
          submitEndChat(null);
        }
      }

      // Task mode: synchronized buffer from elapsed time
      if (featureMode === 'tasks' && roomTask && timerStartedAt && taskTimerMinutes > 0) {
        const taskDurationSeconds = Math.round(taskTimerMinutes * 60);
        const taskBufferSeconds = Math.round(taskBufferTimerMinutes * 60);
        const taskBufferEndSeconds = taskDurationSeconds + taskBufferSeconds;

        if (elapsedSeconds >= taskDurationSeconds) {
          if (!bufferShowingRef.current && !bufferModalDismissedRef.current) {
            bufferShowingRef.current = true;
            setBufferModalType('task');
            setShowBufferModal(true);
            setShowTaskIntroModal(false);
            if (bufferModalAutoDismissRef.current) {
              clearTimeout(bufferModalAutoDismissRef.current);
            }
            bufferModalAutoDismissRef.current = setTimeout(() => {
              bufferModalAutoDismissRef.current = null;
              bufferModalDismissedRef.current = true;
              bufferShowingRef.current = false;
              setShowBufferModal(false);
            }, 15000);
            try {
              const token = localStorage.getItem('token');
              axios.post(
                `${API_BASE_URL}/chat/room/${roomId}/task/buffer/notify`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
              ).catch(() => {});
            } catch (_) {}
          }
          if (elapsedSeconds >= taskBufferEndSeconds) {
            triggerBufferEnd();
          }
        }
      }

      // Synchronized topic timer: derive topic from elapsed time
      if (featureMode === 'topics' && allTopics.length > 0 && topicTimerMinutes > 0) {
        const topicDurationSeconds = Math.round(topicTimerMinutes * 60);
        const derivedIndex = Math.floor(elapsedSeconds / topicDurationSeconds);

        // Initialize lastTopicIndexRef on first run (avoids showing modals for past boundaries when joining mid-chat)
        if (lastTopicIndexRef.current < 0) {
          lastTopicIndexRef.current = Math.min(derivedIndex, allTopics.length - 1);
        }

        // Update displayed topic from derived index
        const displayIndex = Math.min(derivedIndex, allTopics.length - 1);
        setCurrentTopicIndex(displayIndex);
        setRoomTopic(allTopics[displayIndex] || null);

        // Buffer boundary: elapsed >= all topics * duration
        if (derivedIndex >= allTopics.length) {
          const bufferBoundarySeconds = allTopics.length * topicDurationSeconds;
          const bufferEndSeconds = bufferBoundarySeconds + Math.round(bufferTimerMinutes * 60);

          if (!bufferShowingRef.current && !bufferModalDismissedRef.current) {
            bufferShowingRef.current = true;
            setBufferModalType('topic');
            setShowBufferModal(true);
            setShowTopicSwitchModal(false);
            setShowWelcomeModal(false);
            if (topicSwitchAutoDismissRef.current) {
              clearTimeout(topicSwitchAutoDismissRef.current);
              topicSwitchAutoDismissRef.current = null;
            }
            if (bufferModalAutoDismissRef.current) {
              clearTimeout(bufferModalAutoDismissRef.current);
            }
            bufferModalAutoDismissRef.current = setTimeout(() => {
              bufferModalAutoDismissRef.current = null;
              bufferModalDismissedRef.current = true;
              bufferShowingRef.current = false;
              setShowBufferModal(false);
            }, 15000);
            try {
              const token = localStorage.getItem('token');
              axios.post(
                `${API_BASE_URL}/chat/room/${roomId}/topic/buffer/notify`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
              ).catch(() => {});
            } catch (_) {}
          }
          if (elapsedSeconds >= bufferEndSeconds) {
            triggerBufferEnd();
          }
          return;
        }

        // Topic boundary: crossed to next topic
        if (derivedIndex > lastTopicIndexRef.current) {
          lastTopicIndexRef.current = derivedIndex;
          if (!modalShowingRef.current) {
            modalShowingRef.current = true;
            setShowTopicSwitchModal(true);
            setShowWelcomeModal(false);
            // 30-second auto-dismiss is handled by useEffect
          }
        }
      }
    };
    
    compute();
    id = setInterval(compute, 1000);
    return () => { if (id) clearInterval(id); };
  }, [timerStartedAt, roomId, featureMode, allTopics, topicTimerMinutes, bufferTimerMinutes, roomTask, taskTimerMinutes, taskBufferTimerMinutes, triggerBufferEnd, maxChatSeconds, user?.user_type, submitEndChat]);

  // Cleanup topic switch and buffer modal auto-dismiss on unmount
  useEffect(() => {
    return () => {
      if (topicSwitchAutoDismissRef.current) {
        clearTimeout(topicSwitchAutoDismissRef.current);
      }
      if (bufferModalAutoDismissRef.current) {
        clearTimeout(bufferModalAutoDismissRef.current);
      }
    };
  }, []);

  // Reset buffer state when room changes
  useEffect(() => {
    bufferEndTriggeredRef.current = false;
    bufferModalDismissedRef.current = false;
    timeUpTriggeredRef.current = false;
    setTimeUp(false);
  }, [roomId]);

  // Removed: separate baseline clear (handled on mount above)

  // Save a local snapshot of client time whenever we fetch timer so we can continue the count while away
  useEffect(() => {
    try {
      localStorage.setItem(`room_${roomId}_client_now_at_fetch`, String(Date.now()));
    } catch (_) {}
  }, [roomId, timerStartedAt]);

  const handleMessageInputChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);
    if (!connected) return;
    emitTypingState(true);
    if (stopTypingBroadcastTimeoutRef.current) {
      clearTimeout(stopTypingBroadcastTimeoutRef.current);
    }
    stopTypingBroadcastTimeoutRef.current = setTimeout(() => {
      emitTypingState(false);
    }, 2500);
  };

  const sendMessage = async (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    if (!newMessage.trim() || sending || !roomId || !connected || timeUp) return;

    setSending(true);
    
    // Check if this is the first message (timer not started yet)
    const isFirstMessage = !timerStartedAt;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE_URL}/chat/room/${roomId}/message`, 
        { 
          content: newMessage.trim(),
          room_id: parseInt(roomId)
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Optimistically append the new message so the sender sees it immediately
      if (response && response.data) {
        setMessages((prev) => {
          const alreadyExists = prev.some(msg => msg.id === response.data.id);
          return alreadyExists ? prev : [...prev, response.data];
        });
      }
      
      // If this was the first message, initialize timer to 00:00 immediately
      // (the WebSocket event will sync it properly)
      if (isFirstMessage) {
        const now = new Date().toISOString();
        setTimerStartedAt(now);
        setElapsed('00:00');
        try {
          const nowMs = Date.now();
          localStorage.setItem(`room_${roomId}_timer_started_at`, now);
          localStorage.setItem(`room_${roomId}_timer_started_at_ms`, String(nowMs));
          localStorage.setItem(`room_${roomId}_client_start_ms`, String(nowMs));
        } catch (_) {}
        
        // Topic timer is derived from elapsed time in the interval effect - no separate start needed
      }
      
      setNewMessage('');
      if (stopTypingBroadcastTimeoutRef.current) {
        clearTimeout(stopTypingBroadcastTimeoutRef.current);
      }
      emitTypingState(false);
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
      setTimeout(() => {
        if (messageInputRef.current && !messageInputRef.current.disabled) {
          messageInputRef.current.focus();
        }
      }, 10);
    }
  };

  // Removed per-message timestamp display as requested

  const getLanguageName = (code) => {
    const languages = {
      'en': 'English', 'es': 'Spanish', 'fr': 'French', 'de': 'German',
      'it': 'Italian', 'pt': 'Portuguese', 'ru': 'Russian', 'zh': 'Chinese',
      'ja': 'Japanese', 'ko': 'Korean', 'ar': 'Arabic', 'hi': 'Hindi', 'bn': 'Bengali'
    };
    return languages[code] || code;
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <h3 style={styles.loadingText}>Loading chat room...</h3>
      </div>
    );
  }

  const resolvedHeaderHeight = isMobile ? Math.max(mobileHeaderHeight || 0, 64) : 0;
  const resolvedInputHeight = isMobile ? Math.max(mobileInputHeight || 0, 64) : 0;
  const messagesTopOffset = isMobile ? viewportOffsetTop + resolvedHeaderHeight : 0;
  const messagesBottomOffset = isMobile ? resolvedInputHeight + keyboardOffset : 0;
  const mobileMessagesPaddingBottom = isMobile
    ? `${resolvedInputHeight + keyboardOffset + 32}px`
    : '20px';
  const mobileMessagesScrollPaddingBottom = isMobile
    ? `${resolvedInputHeight + keyboardOffset + 48}px`
    : undefined;
  const mobileMessagesPaddingLeft = isMobile
    ? 'calc(12px + env(safe-area-inset-left, 0px))'
    : '20px';
  const mobileMessagesPaddingRight = isMobile
    ? 'calc(12px + env(safe-area-inset-right, 0px))'
    : '20px';
  const mobileMessagesPaddingTop = isMobile ? '12px' : '20px';

  const renderSidebarContent = (isDrawer = false) => (
    <>
      {isDrawer && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
          paddingBottom: 12,
          borderBottom: '1px solid #e5e7eb',
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 2
        }}>
          <div style={{ fontWeight: 700, fontSize: 18, color: '#111827' }}>
            {featureMode === 'topics' ? '💡 Topics' : '📝 Task'}
          </div>
          <button
            onClick={() => setShowMobileSidebar(false)}
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              border: 'none',
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white',
              fontSize: 18,
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)'
            }}
          >
            ✕
          </button>
        </div>
      )}

      {roleLabel && (
        <div style={{
          ...styles.taskCard,
          marginBottom: 12,
          border: `2px solid ${user?.user_type === 'guesser' ? '#3b82f6' : '#f59e0b'}`,
          background: user?.user_type === 'guesser' ? '#eff6ff' : '#fffbeb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>{roleLabel}</div>
          </div>
          {roleInstructions ? (
            <div style={{ marginTop: 8, fontSize: 13, color: '#374151', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
              {renderRoleInstructions(roleInstructions)}
            </div>
          ) : (
            <div style={{ marginTop: 8, fontSize: 13, color: '#6b7280' }}>
              No instructions have been set for your role yet.
            </div>
          )}
        </div>
      )}

      {renderBonusCard(false)}

      <div style={styles.taskCard}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={styles.taskIcon}>ℹ️</div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Instructions</div>
        </div>
        <ul style={{ marginTop: 8, paddingLeft: 18, fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
          <li>The timer starts at 00:00 when the first message is sent.</li>
          <li>The chat lasts at most {maxChatMinutes} minutes, then it ends automatically.</li>
          <li>You must chat in English.</li>
          <li>Do not share sensitive personal information.</li>
          <li>Be respectful. Keep the conversation collaborative.</li>
          <li>Click "End Chat" to finish{user?.user_type === 'guesser' ? '; you will be asked for your final verdict' : ''}.</li>
          {featureMode === 'tasks' && <li>If a task is shown, please stick to it.</li>}
          {featureMode === 'topics' && allTopics.length > 0 && <li>All {allTopics.length} topics are listed below.</li>}
          {featureMode === 'topics' && (
            <li>Discuss each topic for {topicTimerMinutes} {topicTimerMinutes === 1 ? 'minute' : 'minutes'}. You'll be notified to switch topics.</li>
          )}
        </ul>
      </div>

      {featureMode === 'tasks' && taskFeature && (
        <div style={{ ...styles.taskCard, marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={styles.taskIcon}>📝</div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Conversation Task</div>
          </div>
          {!roomTask ? (
            <div style={{ marginTop: 10, color: '#6b7280', fontSize: 13 }}>No task assigned for this room.</div>
          ) : (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontWeight: 700 }}>{roomTask.title}</div>
              <div style={{ marginTop: 6, fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{roomTask.description}</div>
            </div>
          )}
        </div>
      )}

      {featureMode === 'topics' && (
        <div style={{ ...styles.taskCard, marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <div style={styles.taskIcon}>💡</div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Discussion Topics</div>
          </div>
          {allTopics.length === 0 ? (
            <div style={{ color: '#6b7280', fontSize: 13 }}>No topics assigned for this room.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {allTopics.map((topic, index) => (
                <div 
                  key={topic.id || index}
                  style={{
                    padding: 12,
                    borderRadius: 10,
                    background: '#ffffff',
                    border: '1px solid #e5e7eb',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ 
                      minWidth: 24, 
                      height: 24, 
                      borderRadius: '50%',
                      background: '#e5e7eb',
                      color: '#6b7280',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                      fontWeight: 700,
                      flexShrink: 0
                    }}>
                      {index + 1}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontWeight: 600, 
                        fontSize: 13, 
                        color: '#1f2937',
                        marginBottom: 4
                      }}>
                        {topic.title}
                      </div>
                      <div style={{ 
                        fontSize: 11, 
                        color: '#6b7280', 
                        lineHeight: 1.4
                      }}>
                        {topic.description}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );

  return (
    <div style={{
      ...styles.container,
      // Mobile: use fixed positioning to prevent keyboard push
      position: isMobile ? 'fixed' : 'relative',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      height: isMobile ? '100%' : '100vh',
      paddingLeft: isMobile ? 'env(safe-area-inset-left, 0px)' : 0,
      paddingRight: isMobile ? 'env(safe-area-inset-right, 0px)' : 0,
      boxSizing: 'border-box',
      width: '100%',
      maxWidth: '100vw',
      overflow: 'hidden'
    }}>

      {/* Mobile sidebar overlay */}
      {isMobile && showMobileSidebar && (
        <div 
          onClick={() => setShowMobileSidebar(false)}
          style={styles.mobileSidebarOverlay}
        >
          <div 
            style={styles.mobileSidebarPanel}
            onClick={(e) => e.stopPropagation()}
          >
            {renderSidebarContent(true)}
          </div>
        </div>
      )}

      <div style={{
        ...styles.layoutRow,
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        {/* Task Sidebar on the left */}
        <aside style={{
          ...styles.taskSidebar,
          display: isMobile ? 'none' : 'block'
        }}>
          {renderSidebarContent(false)}
        </aside>
        <div style={{
          ...styles.chatContainer,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          overflowX: 'hidden',
          height: isMobile ? '100%' : '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative'
        }}>
        {/* Header */}
        {isMobile ? (
          /* Mobile Header - fixed at top */
          <div 
            id="mobile-chat-header"
            ref={mobileHeaderRef}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '10px 12px',
              paddingTop: 'max(12px, env(safe-area-inset-top, 12px))',
              paddingLeft: 'calc(12px + env(safe-area-inset-left, 0px))',
              paddingRight: 'calc(12px + env(safe-area-inset-right, 0px))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              boxSizing: 'border-box',
              boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
              position: 'fixed',
              top: viewportOffsetTop,
              left: 0,
              right: 0,
              zIndex: 1000,
              width: '100%',
              transform: 'translateZ(0)',
              WebkitTransform: 'translateZ(0)',
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden'
            }}>
            {/* Left: Back + Partner */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
              <button 
                onClick={() => {
                  try {
                    const uid = (user && user.id) || (JSON.parse(localStorage.getItem('user') || '{}')?.id);
                    if (uid) {
                      const key = `unreadByRoom_${uid}`;
                      const raw = localStorage.getItem(key);
                      const map = raw ? (JSON.parse(raw) || {}) : {};
                      if (map[String(roomId)]) { delete map[String(roomId)]; localStorage.setItem(key, JSON.stringify(map)); }
                    }
                  } catch (_) {}
                  try { localStorage.setItem('lastSeenRoomId', String(roomId)); } catch (_) {}
                  clearTimerPersistence(roomId);
                  navigate('/chat');
                  setTimeout(() => { try { window.location.reload(); } catch(e) {} }, 60);
                }}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: 8,
                  width: 34,
                  height: 34,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: 16,
                  cursor: 'pointer',
                  flexShrink: 0
                }}
              >
                ←
              </button>
              
              <div style={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: 13,
                fontWeight: 'bold',
                flexShrink: 0,
                border: '2px solid rgba(255,255,255,0.3)'
              }}>
                {chatPartner?.id ? userInitial(chatPartner) : '?'}
              </div>
              
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{
                  color: 'white',
                  fontSize: 14,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {chatPartner?.id ? userLabel(chatPartner) : 'Partner'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{
                    fontSize: 10,
                    color: connected ? '#86efac' : '#fca5a5',
                    fontWeight: 500
                  }}>
                    {connected ? '● Online' : '○ Offline'}
                  </span>
                  <span style={{
                    background: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: 8,
                    fontSize: 10,
                    fontWeight: 600
                  }}>
                    {elapsed}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Right: Topics + End */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
              {user?.user_type === 'guesser' && (
                <span style={{
                  background: 'rgba(16,185,129,0.9)', color: 'white',
                  padding: '4px 8px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                  whiteSpace: 'nowrap',
                }} title="Your current bonus. Each question you ask lowers it.">
                  💰 {liveGuesserBonus}¢
                </span>
              )}
              {(featureMode === 'tasks' || featureMode === 'topics' || roleLabel) && (
                <button
                  onClick={() => setShowMobileSidebar(!showMobileSidebar)}
                  style={{
                    background: showMobileSidebar ? '#ef4444' : 'rgba(255,255,255,0.15)',
                    border: 'none',
                    borderRadius: 8,
                    padding: '6px 10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 4,
                    color: 'white',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  {showMobileSidebar ? '✕ Close' : (
                    <>
                      <span>{featureMode === 'topics' ? '💡' : (featureMode === 'tasks' ? '📝' : 'ℹ️')}</span>
                      <span>{featureMode === 'topics' ? 'Topics' : (featureMode === 'tasks' ? 'Task' : 'Guide')}</span>
                    </>
                  )}
                </button>
              )}
              
              {user?.user_type !== 'pretender' && (
                <button
                  onClick={handleEndChatClick}
                  style={{
                    background: '#ef4444',
                    border: 'none',
                    borderRadius: 8,
                    padding: '6px 12px',
                    color: 'white',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    boxShadow: '0 2px 6px rgba(239,68,68,0.3)'
                  }}
                >
                  End Chat
                </button>
              )}
            </div>
          </div>
        ) : (
          /* Desktop Header */
          <div style={{...styles.header, flexShrink: 0}}>
            <div style={styles.headerLeft}>
              <button onClick={() => {
                try {
                  const uid = (user && user.id) || (JSON.parse(localStorage.getItem('user') || '{}')?.id);
                  if (uid) {
                    const key = `unreadByRoom_${uid}`;
                    const raw = localStorage.getItem(key);
                    const map = raw ? (JSON.parse(raw) || {}) : {};
                    if (map[String(roomId)]) { delete map[String(roomId)]; localStorage.setItem(key, JSON.stringify(map)); }
                  }
                } catch (_) {}
                try { localStorage.setItem('lastSeenRoomId', String(roomId)); } catch (_) {}
                clearTimerPersistence(roomId);
                navigate('/chat');
                setTimeout(() => { try { window.location.reload(); } catch(e) {} }, 60);
              }} style={styles.backButton}>
                ← Back to Users
              </button>
              <div style={styles.chatInfo}>
                {chatPartner ? (
                  <>
                    <div style={styles.partnerAvatar}>
                      {userInitial(chatPartner)}
                    </div>
                    <div style={styles.partnerDetails}>
                      <h3 style={styles.partnerName}>{userLabel(chatPartner)}</h3>
                      <p style={styles.partnerMeta}>
                        <span style={styles.connectionStatus}>
                          {connected ? '🟢' : '🔴'}
                        </span>
                      </p>
                    </div>
                  </>
                ) : (
                  <div style={styles.partnerDetails}>
                    <h3 style={styles.partnerName}>Private Chat</h3>
                    <p style={styles.partnerMeta}>
                      <span style={styles.connectionStatus}>
                        {connected ? '🟢' : '🔴'}
                      </span>
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            <div style={styles.headerRight}>
              <span style={styles.currentUser}>{userLabel(user)}</span>
              <span style={styles.timerChip} title={timerStartedAt ? new Date(timerStartedAt).toLocaleString() : 'Timer not started'}>⏱️ {elapsed}</span>
              {user?.user_type === 'guesser' && (
                <span
                  style={{ ...styles.timerChip, background: 'rgba(16,185,129,0.15)', color: '#065f46', fontWeight: 700 }}
                  title="Your current bonus. Each question you ask lowers it."
                >
                  💰 {liveGuesserBonus}¢
                </span>
              )}
              {user?.user_type !== 'pretender' && (
                <button
                  onClick={handleEndChatClick}
                  style={{
                    backgroundColor: '#ef4444', color: 'white', border: 'none',
                    padding: '8px 16px',
                    borderRadius: '8px', cursor: 'pointer',
                    fontSize: '14px', fontWeight: 600
                  }}
                >
                  End Chat
                </button>
              )}
              <button onClick={logout} style={styles.logoutButton}>
                Logout
              </button>
            </div>
          </div>
        )}

        {/* Messages */}
        <div 
          id="mobile-chat-messages"
          ref={messagesContainerRef}
          style={{
            ...styles.messagesContainer,
            paddingTop: mobileMessagesPaddingTop,
            paddingBottom: mobileMessagesPaddingBottom,
            paddingLeft: isMobile ? mobileMessagesPaddingLeft : '20px',
            paddingRight: isMobile ? mobileMessagesPaddingRight : '20px',
            flex: isMobile ? undefined : 1,
            minHeight: isMobile ? undefined : 0,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: isMobile ? '100vw' : 'none',
            scrollPaddingBottom: mobileMessagesScrollPaddingBottom,
            touchAction: 'pan-y',
            overscrollBehaviorY: 'contain',
            position: isMobile ? 'fixed' : 'relative',
            top: isMobile ? `${messagesTopOffset}px` : 'auto',
            left: isMobile ? 0 : 'auto',
            right: isMobile ? 0 : 'auto',
            bottom: isMobile ? `${messagesBottomOffset}px` : 'auto',
            zIndex: isMobile ? 10 : 'auto',
            transition: isMobile ? 'bottom 0.2s ease-out' : 'none'
          }}>
          {/* Spacer to push messages to bottom when few messages */}
          <div style={{ flexGrow: 1, minHeight: 0 }} />
          {messages.length === 0 ? (
            <div style={styles.emptyState}>
              <div style={{
                ...styles.emptyIcon,
                fontSize: isMobile ? '48px' : '64px'
              }}>💬</div>
              <h3 style={{
                ...styles.emptyTitle,
                fontSize: isMobile ? '20px' : '24px'
              }}>Start your conversation!</h3>
              <p style={styles.emptyText}>Send a message to begin chatting.</p>
            </div>
          ) : (
            messages.map((message) => {
              const currentUserId = Number(user?.id);
              const senderId = (message?.sender && message.sender.id) ?? message.sender_id;
              const isMine = senderId === currentUserId;
              return (
                <div
                  key={message.id}
                  style={{
                    ...styles.messageWrapper,
                    alignSelf: isMine ? 'flex-end' : 'flex-start',
                    maxWidth: isMobile ? '90%' : '75%'
                  }}
                >
                  <div
                    style={{
                      ...styles.message,
                      ...(isMine ? styles.myMessage : styles.otherMessage)
                    }}
                  >
                    {/* Sender name hidden as requested */}
                    
                    <div style={styles.messageContent}>
                      {message.original_content || message.content}
                    </div>
                    
                    {/* Per-message time hidden */}
                  </div>
                </div>
              );
            })
          )}
          {isPartnerTyping && (
            <div style={styles.typingIndicatorRow}>
              <div
                style={styles.typingBubble}
                aria-live="polite"
                aria-label={`${chatPartner?.id ? userLabel(chatPartner) : 'Partner'} is typing`}
              >
                <div style={styles.typingDotTrack}>
                  <span className="typing-dot"></span>
                  <span className="typing-dot"></span>
                  <span className="typing-dot"></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {timeUp && (
          <div style={{
            flexShrink: 0,
            padding: isMobile ? '10px 12px' : '12px 30px',
            background: '#fef2f2',
            borderTop: '1px solid #fecaca',
            color: '#b91c1c',
            fontSize: isMobile ? 13 : 14,
            fontWeight: 600,
            textAlign: 'center',
          }}>
            ⏰ Time's up ({maxChatMinutes} minutes).{' '}
            {user?.user_type === 'guesser'
              ? 'Please give your final verdict to finish.'
              : user?.user_type === 'pretender'
                ? 'Waiting for the other participant to submit their verdict…'
                : 'The chat is ending.'}
          </div>
        )}

        {/* Message Input */}
        <form
          id="mobile-chat-input"
          ref={mobileInputRef}
          onSubmit={sendMessage} 
          style={{
            ...styles.inputForm,
            padding: isMobile ? '10px 12px' : '20px 30px',
            paddingBottom: isMobile 
              ? (keyboardOffset > 0 ? '10px' : 'max(10px, env(safe-area-inset-bottom, 0px))')
              : '20px',
            boxSizing: 'border-box',
            flexShrink: 0,
            backgroundColor: 'white',
            borderTop: '1px solid #e5e7eb',
            position: isMobile ? 'fixed' : 'relative',
            bottom: isMobile ? `${keyboardOffset}px` : 'auto',
            left: isMobile ? 0 : 'auto',
            right: isMobile ? 0 : 'auto',
            zIndex: isMobile ? 9999 : 'auto',
            transition: isMobile ? 'bottom 0.2s ease-out' : 'none'
          }}>
          <div style={{
            ...styles.inputContainer,
            gap: isMobile ? '10px' : '15px'
          }}>
                <input
                  ref={messageInputRef}
                  type="text"
                  value={newMessage}
                  onChange={handleMessageInputChange}
                  onFocus={() => {
                    // Scroll to bottom when input is focused (keyboard opens)
                    if (isMobile) {
                      setTimeout(() => {
                        scrollToBottom('smooth');
                      }, 250);
                    }
                  }}
                  onBlur={() => {
                    emitTypingState(false);
                    if (stopTypingBroadcastTimeoutRef.current) {
                      clearTimeout(stopTypingBroadcastTimeoutRef.current);
                    }
                  }}
                  placeholder={timeUp ? `Time's up (${maxChatMinutes} min) — chatting is closed` : `Type your message...`}
                  style={{
                    ...styles.messageInput,
                    padding: isMobile ? '10px 14px' : '15px 20px',
                    fontSize: isMobile ? '16px' : '16px', // 16px prevents iOS zoom
                    minWidth: 0,
                    boxSizing: 'border-box'
                  }}
                  maxLength={1000}
                  disabled={!connected || timeUp}
                />
            <button
              type="submit"
              disabled={!newMessage.trim() || sending || !connected || timeUp}
              onMouseDown={(e) => e.preventDefault()}
              onTouchStart={() => {
                if (messageInputRef.current && !messageInputRef.current.disabled) {
                  messageInputRef.current.focus();
                }
              }}
              style={{
                ...styles.sendButton,
                ...((!newMessage.trim() || sending || !connected || timeUp) ? styles.sendButtonDisabled : {}),
                width: isMobile ? 42 : 50,
                height: isMobile ? 42 : 50,
                minWidth: isMobile ? 42 : 50,
                fontSize: isMobile ? 16 : 18,
                flexShrink: 0
              }}
            >
              {sending ? '⏳' : '📤'}
            </button>
          </div>
        </form>
        </div>
      </div>
      {/* Preference modal removed as messages default to receiver native language */}
      
      {/* Topic Switch Notification Modal */}
      {showTopicSwitchModal && featureMode === 'topics' && allTopics.length > 0 && (
        <div style={styles.modalOverlay}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: isMobile ? 16 : 24,
            padding: isMobile ? '20px 16px' : '32px 40px',
            maxWidth: isMobile ? '92%' : 480,
            width: '100%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
            animation: 'slideDown 0.3s ease-out'
          }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: isMobile ? 18 : 24 }}>
              <div style={{ 
                fontSize: isMobile ? 44 : 52, 
                marginBottom: isMobile ? 8 : 10,
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
              }}>✨</div>
              <h2 style={{ 
                fontSize: isMobile ? 22 : 26, 
                fontWeight: 800, 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                marginBottom: 6
              }}>
                Time for Next Topic
              </h2>
              <p style={{ 
                fontSize: isMobile ? 14 : 15, 
                color: '#6b7280',
                margin: 0,
                lineHeight: 1.4
              }}>
                You may start talking about the next topic
              </p>
            </div>
            
            {/* Topic Just Completed */}
            {currentTopicIndex > 0 && allTopics[currentTopicIndex - 1] && (
              <div style={{
                background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                border: '2px solid #fecaca',
                borderRadius: isMobile ? 12 : 16,
                padding: isMobile ? 14 : 18,
                marginBottom: isMobile ? 14 : 18,
                boxShadow: '0 2px 12px rgba(239, 68, 68, 0.08)'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: 12
                }}>
                  <span style={{ 
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)', 
                    color: 'white', 
                    width: isMobile ? 28 : 32, 
                    height: isMobile ? 28 : 32, 
                    borderRadius: '50%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: isMobile ? 12 : 14,
                    fontWeight: 800,
                    flexShrink: 0,
                    boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)'
                  }}>✓</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      fontSize: isMobile ? 10 : 11, 
                      color: '#991b1b', 
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: 4
                    }}>Just Completed</div>
                    <div style={{ 
                      fontSize: isMobile ? 15 : 17, 
                      fontWeight: 700, 
                      color: '#7f1d1d',
                      lineHeight: 1.3
                    }}>
                      {allTopics[currentTopicIndex - 1]?.title}
                    </div>
                    {allTopics[currentTopicIndex - 1]?.description && (
                      <div style={{ 
                        fontSize: isMobile ? 12 : 13, 
                        color: '#b91c1c', 
                        marginTop: 6,
                        opacity: 0.9,
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {allTopics[currentTopicIndex - 1]?.description}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Next Topic */}
            {currentTopicIndex < allTopics.length && allTopics[currentTopicIndex] && (
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: isMobile ? 12 : 16,
                padding: isMobile ? 16 : 20,
                marginBottom: isMobile ? 18 : 22,
                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.35)'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 10,
                  marginBottom: 8
                }}>
                  <span style={{ 
                    background: 'rgba(255,255,255,0.3)', 
                    color: 'white', 
                    width: isMobile ? 28 : 32, 
                    height: isMobile ? 28 : 32, 
                    borderRadius: '50%', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: isMobile ? 12 : 14,
                    fontWeight: 800,
                    flexShrink: 0,
                    border: '2px solid rgba(255,255,255,0.5)'
                  }}>→</span>
                  <span style={{ 
                    fontSize: isMobile ? 11 : 12, 
                    color: 'rgba(255,255,255,0.95)', 
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Up Next</span>
                </div>
                <div style={{ 
                  fontSize: isMobile ? 17 : 19, 
                  fontWeight: 800, 
                  color: 'white',
                  marginBottom: 6,
                  lineHeight: 1.3
                }}>
                  {allTopics[currentTopicIndex]?.title}
                </div>
                {allTopics[currentTopicIndex]?.description && (
                  <div style={{ 
                    fontSize: isMobile ? 13 : 14, 
                    color: 'rgba(255,255,255,0.9)', 
                    lineHeight: 1.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>
                    {allTopics[currentTopicIndex]?.description}
                  </div>
                )}
              </div>
            )}
            
            {/* OK Button - Compact */}
            <button
              onClick={handleNextTopic}
              style={{
                width: '100%',
                padding: isMobile ? '14px' : '16px',
                fontSize: isMobile ? 15 : 16,
                fontWeight: 700,
                color: 'white',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                border: 'none',
                borderRadius: isMobile ? 10 : 12,
                cursor: 'pointer',
                boxShadow: '0 4px 16px rgba(16, 185, 129, 0.35)',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(16, 185, 129, 0.45)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 16px rgba(16, 185, 129, 0.35)';
              }}
            >
              {currentTopicIndex < allTopics.length - 1 ? '✨ OK - Next Topic' : '✅ OK - Done'}
            </button>
            
            <div style={{ 
              marginTop: isMobile ? 10 : 12, 
              textAlign: 'center', 
              fontSize: isMobile ? 10 : 11, 
              color: '#9ca3af' 
            }}>
              {currentTopicIndex < allTopics.length - 1 
                ? `Topic ${currentTopicIndex + 2}/${allTopics.length} • ${topicTimerMinutes} min each`
                : `All ${allTopics.length} topics done • Buffer time next`
              }
              <div style={{ marginTop: 4 }}>Dismisses automatically in 30 seconds</div>
            </div>
          </div>
        </div>
      )}

      {/* Task Intro Modal - first time entering chat in task mode */}
      {showTaskIntroModal && roomTask && (
        <div style={styles.modalOverlay}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: isMobile ? 20 : 32,
            padding: isMobile ? '28px 20px' : '48px 56px',
            maxWidth: isMobile ? '95%' : 600,
            width: '100%',
            maxHeight: isMobile ? '92vh' : '88vh',
            overflowY: 'auto',
            boxShadow: '0 30px 100px rgba(0, 0, 0, 0.4)',
            animation: 'slideDown 0.4s ease-out'
          }}>
            <div style={{ textAlign: 'center', marginBottom: isMobile ? 20 : 28 }}>
              <div style={{ fontSize: isMobile ? 48 : 64, marginBottom: 12 }}>💬</div>
              <h2 style={{ 
                fontSize: isMobile ? 22 : 28, 
                fontWeight: 800, 
                color: '#1f2937',
                marginBottom: 12
              }}>
                Welcome to the Chat!
              </h2>
              <p style={{ fontSize: isMobile ? 14 : 16, color: '#6b7280', marginBottom: 20 }}>
                Please read the rules first, then your task below.
              </p>
            </div>

            {/* Rules first */}
            <div style={{
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
              border: '2px solid #7dd3fc',
              borderRadius: isMobile ? 14 : 18,
              padding: isMobile ? 18 : 24,
              marginBottom: isMobile ? 20 : 28
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 10, 
                marginBottom: isMobile ? 14 : 18 
              }}>
                <span style={{ fontSize: isMobile ? 22 : 28 }}>📋</span>
                <h3 style={{ 
                  fontSize: isMobile ? 17 : 20, 
                  fontWeight: 700, 
                  color: '#0369a1',
                  margin: 0
                }}>Chat Rules</h3>
              </div>
              <ul style={{ 
                margin: 0, 
                paddingLeft: isMobile ? 20 : 24, 
                fontSize: isMobile ? 13 : 15, 
                color: '#0c4a6e', 
                lineHeight: 1.8,
                listStyleType: 'none'
              }}>
                <li style={{ marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <span>⏱️</span>
                  <span>The timer starts at 00:00 when the first message is sent</span>
                </li>
                <li style={{ marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <span>🗣️</span>
                  <span>You must chat in English</span>
                </li>
                <li style={{ marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <span>🔒</span>
                  <span>Do not share sensitive personal information</span>
                </li>
                <li style={{ marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <span>🤝</span>
                  <span>Be respectful and keep the conversation collaborative</span>
                </li>
                <li style={{ marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <span>🚪</span>
                  <span>Click "End Chat" when finished to complete your session</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <span>📝</span>
                  <span>If a task is shown, please stick to it</span>
                </li>
              </ul>
            </div>

            {/* Role instructions */}
            {roleLabel && (
              <div style={{
                background: user?.user_type === 'guesser'
                  ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)'
                  : 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                border: `2px solid ${user?.user_type === 'guesser' ? '#3b82f6' : '#f59e0b'}`,
                borderRadius: isMobile ? 14 : 18,
                padding: isMobile ? 18 : 24,
                marginBottom: isMobile ? 20 : 28
              }}>
                <h3 style={{ fontSize: isMobile ? 17 : 20, fontWeight: 700, color: '#1f2937', margin: 0, marginBottom: 10 }}>
                  {roleLabel}
                </h3>
                <div style={{ fontSize: isMobile ? 13 : 15, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {roleInstructions ? renderRoleInstructions(roleInstructions) : 'No instructions have been set for your role yet.'}
                </div>
              </div>
            )}

            {/* Bonus reward card (second instruction card) */}
            {renderBonusCard(true)}

            {/* Task info second */}
            <div style={{
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              border: '2px solid #f59e0b',
              borderRadius: isMobile ? 14 : 18,
              padding: isMobile ? 18 : 24,
              marginBottom: isMobile ? 20 : 28
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 10, 
                marginBottom: isMobile ? 14 : 18 
              }}>
                <span style={{ fontSize: isMobile ? 22 : 28 }}>📝</span>
                <h3 style={{ 
                  fontSize: isMobile ? 17 : 20, 
                  fontWeight: 700, 
                  color: '#92400e',
                  margin: 0
                }}>Your Task</h3>
              </div>
              <div style={{ fontWeight: 700, fontSize: isMobile ? 16 : 18, color: '#92400e', marginBottom: 8 }}>
                {roomTask.title}
              </div>
              <div style={{ fontSize: isMobile ? 13 : 15, color: '#a16207', lineHeight: 1.6 }}>
                {roomTask.description}
              </div>
            </div>
            <div style={{ 
              padding: 12, 
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: 8,
              marginBottom: 24,
              fontSize: 13,
              color: '#1e40af'
            }}>
              ⏱️ Task time: <strong>{taskTimerMinutes} {taskTimerMinutes === 1 ? 'minute' : 'minutes'}</strong> • Buffer time: <strong>{formatBufferTime(taskBufferTimerMinutes)}</strong>
            </div>
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={() => {
                  taskIntroShownRef.current = true;
                  if (taskIntroStorageKeyRef.current) {
                    try { sessionStorage.setItem(taskIntroStorageKeyRef.current, '1'); } catch (_) {}
                  }
                  setShowTaskIntroModal(false);
                }}
                style={{
                  padding: isMobile ? '16px 48px' : '18px 64px',
                  fontSize: isMobile ? 16 : 18,
                  fontWeight: 700,
                  color: 'white',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: isMobile ? 12 : 16,
                  cursor: 'pointer',
                  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                  transition: 'all 0.3s ease',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                ✨ Let's Start
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Buffer Modal - after all topics covered */}
      {/* Cultural Turing Test: Guesser's final verdict before ending the chat */}
      {showVerdictModal && (
        <div style={styles.modalOverlay}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: isMobile ? 20 : 32,
            padding: isMobile ? '24px 18px' : '36px 44px',
            maxWidth: isMobile ? '95%' : 540,
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 30px 100px rgba(0, 0, 0, 0.4)',
            animation: 'slideDown 0.4s ease-out'
          }}>
            <div style={{ textAlign: 'center', marginBottom: isMobile ? 16 : 20 }}>
              <div style={{ fontSize: isMobile ? 40 : 52, marginBottom: 8 }}>🕵️</div>
              <h2 style={{ fontSize: isMobile ? 21 : 26, fontWeight: 800, color: '#1f2937', marginBottom: 8 }}>
                Your final verdict
              </h2>
              <p style={{ fontSize: isMobile ? 13 : 15, color: '#6b7280', lineHeight: 1.5, margin: 0 }}>
                Answer these questions to finish. This ends the chat.
              </p>
            </div>

            {/* Q1: German verdict */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: isMobile ? 14 : 15, fontWeight: 700, color: '#1f2937', marginBottom: 8 }}>
                Is <strong>{chatPartner?.id ? userLabel(chatPartner) : 'your partner'}</strong> genuinely German?
              </div>
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10 }}>
                {[
                  { val: 'german', label: '🇩🇪 Genuinely German', on: '#059669', bg: '#ecfdf5', bd: '#10b981' },
                  { val: 'not_german', label: '🚫 Not German', on: '#dc2626', bg: '#fef2f2', bd: '#ef4444' },
                ].map(opt => {
                  const sel = verdict === opt.val;
                  return (
                    <button
                      key={opt.val}
                      onClick={() => setVerdict(opt.val)}
                      disabled={endingChat}
                      style={{
                        flex: 1, padding: isMobile ? '13px' : '15px',
                        fontSize: isMobile ? 15 : 16, fontWeight: 700,
                        color: sel ? 'white' : opt.on,
                        background: sel ? opt.on : opt.bg,
                        border: `2px solid ${opt.bd}`, borderRadius: 12,
                        cursor: endingChat ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Q2: Confidence 1-5 */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: isMobile ? 14 : 15, fontWeight: 700, color: '#1f2937', marginBottom: 8 }}>
                How confident are you?
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[1, 2, 3, 4, 5].map(n => {
                  const sel = confidence === n;
                  return (
                    <button
                      key={n}
                      onClick={() => setConfidence(n)}
                      disabled={endingChat}
                      style={{
                        flex: 1, padding: isMobile ? '12px 0' : '14px 0',
                        fontSize: isMobile ? 16 : 18, fontWeight: 700,
                        color: sel ? 'white' : '#4338ca',
                        background: sel ? '#4f46e5' : '#eef2ff',
                        border: `2px solid ${sel ? '#4f46e5' : '#c7d2fe'}`, borderRadius: 12,
                        cursor: endingChat ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                <span>Not confident</span>
                <span>Very confident</span>
              </div>
            </div>

            {/* Q3: Human vs AI */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: isMobile ? 14 : 15, fontWeight: 700, color: '#1f2937', marginBottom: 8 }}>
                Was your partner a real person or an AI?
              </div>
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10 }}>
                {[
                  { val: 'human', label: '🧑 Real person' },
                  { val: 'ai', label: '🤖 An AI' },
                ].map(opt => {
                  const sel = aiSuspicion === opt.val;
                  return (
                    <button
                      key={opt.val}
                      onClick={() => setAiSuspicion(opt.val)}
                      disabled={endingChat}
                      style={{
                        flex: 1, padding: isMobile ? '13px' : '15px',
                        fontSize: isMobile ? 15 : 16, fontWeight: 700,
                        color: sel ? 'white' : '#0369a1',
                        background: sel ? '#0284c7' : '#f0f9ff',
                        border: `2px solid ${sel ? '#0284c7' : '#7dd3fc'}`, borderRadius: 12,
                        cursor: endingChat ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Q4: Reason (required) */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: isMobile ? 14 : 15, fontWeight: 700, color: '#1f2937', marginBottom: 8 }}>
                What made you decide? <span style={{ fontWeight: 400, color: '#ef4444' }}>*</span>
              </div>
              <textarea
                value={verdictReason}
                onChange={(e) => setVerdictReason(e.target.value)}
                disabled={endingChat}
                rows={3}
                maxLength={2000}
                placeholder="E.g. their answers about everyday life, how they used the language, a specific detail…"
                style={{
                  width: '100%', boxSizing: 'border-box', resize: 'vertical',
                  padding: '10px 12px', fontSize: isMobile ? 14 : 15,
                  border: '2px solid #e5e7eb', borderRadius: 12, fontFamily: 'inherit',
                }}
              />
            </div>

            <button
              onClick={() => submitEndChat(verdict, { confidence, ai_suspicion: aiSuspicion, reason: verdictReason })}
              disabled={endingChat || !verdict || !confidence || !aiSuspicion || !verdictReason.trim()}
              style={{
                width: '100%', padding: isMobile ? '15px' : '16px',
                fontSize: isMobile ? 16 : 17, fontWeight: 800, color: 'white',
                background: (!verdict || !confidence || !aiSuspicion || !verdictReason.trim())
                  ? '#9ca3af'
                  : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                border: 'none', borderRadius: 14,
                cursor: (endingChat || !verdict || !confidence || !aiSuspicion || !verdictReason.trim()) ? 'not-allowed' : 'pointer',
                boxShadow: '0 8px 24px rgba(79, 70, 229, 0.35)',
              }}
            >
              {endingChat ? 'Submitting…' : 'Submit verdict & finish'}
            </button>
            <div style={{ textAlign: 'center', marginTop: 10 }}>
              <button
                onClick={() => setShowVerdictModal(false)}
                disabled={endingChat}
                style={{
                  padding: '8px 20px', fontSize: isMobile ? 13 : 14, fontWeight: 600,
                  color: '#6b7280', background: 'transparent', border: 'none',
                  cursor: endingChat ? 'not-allowed' : 'pointer'
                }}
              >
                ← Keep chatting
              </button>
            </div>
          </div>
        </div>
      )}

      {showBufferModal && (
        <div style={styles.modalOverlay}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: isMobile ? 20 : 32,
            padding: isMobile ? '28px 20px' : '48px 56px',
            maxWidth: isMobile ? '95%' : 520,
            width: '100%',
            boxShadow: '0 30px 100px rgba(0, 0, 0, 0.4)',
            animation: 'slideDown 0.4s ease-out'
          }}>
            <div style={{ textAlign: 'center', marginBottom: isMobile ? 20 : 28 }}>
              <div style={{ fontSize: isMobile ? 48 : 64, marginBottom: 12 }}>⏳</div>
              <h2 style={{ 
                fontSize: isMobile ? 22 : 28, 
                fontWeight: 800, 
                color: '#1f2937',
                marginBottom: 12
              }}>
                {bufferModalType === 'task' ? 'Task Time is Up!' : 'All Topics Covered!'}
              </h2>
              <p style={{ 
                fontSize: isMobile ? 14 : 16, 
                color: '#6b7280',
                lineHeight: 1.6,
                margin: 0
              }}>
                {bufferModalType === 'task'
                  ? <>Task time is up. You now have exactly <strong>{formatBufferTime(taskBufferTimerMinutes)}</strong> buffer time. After {formatTotalMinutes(taskTimerMinutes + (parseFloat(taskBufferTimerMinutes) || 5))} total ({formatTotalMinutes(taskTimerMinutes)} task + {formatBufferTime(taskBufferTimerMinutes)} buffer), the chat will end automatically.</>
                  : <>You have covered all topics. You now have exactly <strong>{formatBufferTime(bufferTimerMinutes)}</strong> buffer time. After {formatTotalMinutes(topicTimerMinutes * allTopics.length + (parseFloat(bufferTimerMinutes) || 5))} total ({topicTimerMinutes} min × {allTopics.length} topics + {formatBufferTime(bufferTimerMinutes)} buffer), the chat will end automatically.</>}
              </p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={handleBufferDismiss}
                style={{
                  padding: isMobile ? '16px 48px' : '18px 64px',
                  fontSize: isMobile ? 16 : 18,
                  fontWeight: 700,
                  color: 'white',
                  background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  border: 'none',
                  borderRadius: isMobile ? 12 : 16,
                  cursor: 'pointer',
                  boxShadow: '0 8px 24px rgba(245, 158, 11, 0.4)',
                  transition: 'all 0.3s ease',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                OK
              </button>
              <div style={{ marginTop: 12, fontSize: isMobile ? 11 : 12, color: '#9ca3af' }}>
                Dismisses automatically in 15 seconds
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Welcome Modal - shows when entering chat room */}
      {showWelcomeModal && (
        <div style={styles.modalOverlay}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: isMobile ? 20 : 32,
            padding: isMobile ? '28px 20px' : '56px 64px',
            maxWidth: isMobile ? '95%' : 850,
            width: '100%',
            maxHeight: isMobile ? '92vh' : '88vh',
            overflowY: 'auto',
            boxShadow: '0 30px 100px rgba(0, 0, 0, 0.4)',
            animation: 'slideDown 0.4s ease-out'
          }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: isMobile ? 24 : 32 }}>
              <div style={{ 
                fontSize: isMobile ? 56 : 72, 
                marginBottom: isMobile ? 12 : 16,
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))'
              }}>💬</div>
              <h2 style={{ 
                fontSize: isMobile ? 24 : 32, 
                fontWeight: 800, 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                marginBottom: 8
              }}>
                Welcome to the Chat!
              </h2>
              <p style={{ 
                fontSize: isMobile ? 14 : 16, 
                color: '#6b7280',
                maxWidth: 400,
                margin: '0 auto'
              }}>
                Please read the instructions below before you start chatting
              </p>
            </div>

            {/* Instructions Section (step 1) */}
            {welcomeStep === 1 && (
            <div style={{
              background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
              border: '2px solid #7dd3fc',
              borderRadius: isMobile ? 14 : 18,
              padding: isMobile ? 18 : 24,
              marginBottom: isMobile ? 20 : 28
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 10, 
                marginBottom: isMobile ? 14 : 18 
              }}>
                <span style={{ fontSize: isMobile ? 22 : 28 }}>📋</span>
                <h3 style={{ 
                  fontSize: isMobile ? 17 : 20, 
                  fontWeight: 700, 
                  color: '#0369a1',
                  margin: 0
                }}>Chat Guidelines</h3>
              </div>
              <ul style={{ 
                margin: 0, 
                paddingLeft: isMobile ? 20 : 24, 
                fontSize: isMobile ? 13 : 15, 
                color: '#0c4a6e', 
                lineHeight: 1.8,
                listStyleType: 'none'
              }}>
                <li style={{ marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <span>⏱️</span>
                  <span>The timer starts at 00:00 when the first message is sent</span>
                </li>
                <li style={{ marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <span>🗣️</span>
                  <span>You must chat in English</span>
                </li>
                <li style={{ marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <span>🔒</span>
                  <span>Do not share sensitive personal information</span>
                </li>
                <li style={{ marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <span>🤝</span>
                  <span>Be respectful and keep the conversation collaborative</span>
                </li>
                <li style={{ marginBottom: 8, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  <span>🚪</span>
                  <span>Click "End Chat" when finished to complete your session</span>
                </li>
                {featureMode === 'topics' && allTopics.length > 0 && (
                  <li style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <span>💡</span>
                    <span>Discuss each topic for {topicTimerMinutes} {topicTimerMinutes === 1 ? 'minute' : 'minutes'} - you'll be notified to switch</span>
                  </li>
                )}
              </ul>
            </div>
            )}

            {/* Role instructions (Cultural Turing Test) — step 2 */}
            {welcomeStep === 2 && (
              <div style={{
                background: user?.user_type === 'guesser'
                  ? 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)'
                  : 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                border: `2px solid ${user?.user_type === 'guesser' ? '#3b82f6' : '#f59e0b'}`,
                borderRadius: isMobile ? 14 : 18,
                padding: isMobile ? 18 : 24,
                marginBottom: isMobile ? 20 : 28
              }}>
                <h3 style={{ fontSize: isMobile ? 17 : 20, fontWeight: 700, color: '#1f2937', margin: 0, marginBottom: 10 }}>
                  {roleLabel || 'Your role'}
                </h3>
                <div style={{ fontSize: isMobile ? 13 : 15, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {roleInstructions ? renderRoleInstructions(roleInstructions) : 'No instructions have been set for your role yet.'}
                </div>
              </div>
            )}

            {/* Bonus reward + task/topics (step 3) */}
            {welcomeStep === 3 && (
            <>
            {renderBonusCard(true)}

            {/* Topics Section */}
            {featureMode === 'topics' && allTopics.length > 0 && (
              <div style={{
                background: 'linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%)',
                border: '2px solid #c4b5fd',
                borderRadius: isMobile ? 14 : 18,
                padding: isMobile ? 18 : 24,
                marginBottom: isMobile ? 20 : 28
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 10, 
                  marginBottom: isMobile ? 16 : 20 
                }}>
                  <span style={{ fontSize: isMobile ? 22 : 28 }}>💬</span>
                  <h3 style={{ 
                    fontSize: isMobile ? 17 : 20, 
                    fontWeight: 700, 
                    color: '#7c3aed',
                    margin: 0
                  }}>Discussion Topics ({allTopics.length})</h3>
                </div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 10 : 14 }}>
                  {allTopics.map((topic, index) => (
                    <div 
                      key={topic.id || index}
                      style={{
                        background: index === 0 
                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
                          : 'white',
                        border: index === 0 ? 'none' : '2px solid #e9d5ff',
                        borderRadius: isMobile ? 12 : 14,
                        padding: isMobile ? 14 : 18,
                        boxShadow: index === 0 
                          ? '0 8px 24px rgba(102, 126, 234, 0.35)' 
                          : '0 2px 8px rgba(0,0,0,0.05)'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: isMobile ? 10 : 14 }}>
                        <div style={{ 
                          minWidth: isMobile ? 28 : 36, 
                          height: isMobile ? 28 : 36, 
                          borderRadius: '50%',
                          background: index === 0 
                            ? 'rgba(255,255,255,0.25)' 
                            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: isMobile ? 13 : 15,
                          fontWeight: 700,
                          flexShrink: 0
                        }}>
                          {index + 1}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 8,
                            marginBottom: 4
                          }}>
                            <span style={{ 
                              fontWeight: 700, 
                              fontSize: isMobile ? 14 : 17, 
                              color: index === 0 ? 'white' : '#1f2937'
                            }}>
                              {topic.title}
                            </span>
                            {index === 0 && (
                              <span style={{
                                background: 'rgba(255,255,255,0.25)',
                                color: 'white',
                                fontSize: isMobile ? 9 : 11,
                                fontWeight: 700,
                                padding: '3px 10px',
                                borderRadius: 20,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                Start Here
                              </span>
                            )}
                          </div>
                          <div style={{ 
                            fontSize: isMobile ? 12 : 14, 
                            color: index === 0 ? 'rgba(255,255,255,0.9)' : '#6b7280', 
                            lineHeight: 1.5
                          }}>
                            {topic.description}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Task Section (if tasks mode) */}
            {featureMode === 'tasks' && roomTask && (
              <div style={{
                background: 'linear-gradient(135deg, #fefce8 0%, #fef9c3 100%)',
                border: '2px solid #fde047',
                borderRadius: isMobile ? 14 : 18,
                padding: isMobile ? 18 : 24,
                marginBottom: isMobile ? 20 : 28
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 10, 
                  marginBottom: 12 
                }}>
                  <span style={{ fontSize: isMobile ? 22 : 28 }}>📝</span>
                  <h3 style={{ 
                    fontSize: isMobile ? 17 : 20, 
                    fontWeight: 700, 
                    color: '#a16207',
                    margin: 0
                  }}>Your Task</h3>
                </div>
                <div style={{ 
                  fontWeight: 700, 
                  fontSize: isMobile ? 16 : 18, 
                  color: '#854d0e',
                  marginBottom: 8
                }}>
                  {roomTask.title}
                </div>
                <div style={{ 
                  fontSize: isMobile ? 13 : 15, 
                  color: '#92400e', 
                  lineHeight: 1.6 
                }}>
                  {roomTask.description}
                </div>
              </div>
            )}

            </>
            )}

            {/* Step progress + gated advance button */}
            <div style={{ textAlign: 'center' }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: 8, marginBottom: 14,
              }}>
                {Array.from({ length: WELCOME_TOTAL_STEPS }).map((_, i) => (
                  <span key={i} style={{
                    width: i + 1 === welcomeStep ? 24 : 10, height: 10, borderRadius: 6,
                    background: i + 1 === welcomeStep ? '#667eea' : (i + 1 < welcomeStep ? '#a5b4fc' : '#e5e7eb'),
                    transition: 'all 0.3s ease',
                  }} />
                ))}
                <span style={{ marginLeft: 8, fontSize: 13, color: '#6b7280', fontWeight: 600 }}>
                  Step {welcomeStep} of {WELCOME_TOTAL_STEPS}
                </span>
              </div>
              <button
                disabled={welcomeStepSecondsLeft > 0}
                onClick={() => {
                  if (welcomeStepSecondsLeft > 0) return;
                  if (welcomeStep >= WELCOME_TOTAL_STEPS) {
                    setShowWelcomeModal(false);
                  } else {
                    setWelcomeStep((s) => s + 1);
                  }
                }}
                style={{
                  padding: isMobile ? '16px 48px' : '18px 64px',
                  fontSize: isMobile ? 16 : 18,
                  fontWeight: 700,
                  color: 'white',
                  background: welcomeStepSecondsLeft > 0
                    ? '#9ca3af'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: isMobile ? 12 : 16,
                  cursor: welcomeStepSecondsLeft > 0 ? 'not-allowed' : 'pointer',
                  boxShadow: welcomeStepSecondsLeft > 0 ? 'none' : '0 8px 24px rgba(102, 126, 234, 0.4)',
                  transition: 'all 0.3s ease',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                {welcomeStepSecondsLeft > 0
                  ? `Please read… (${welcomeStepSecondsLeft})`
                  : (welcomeStep >= WELCOME_TOTAL_STEPS ? "✨ Let's Start Chatting!" : 'Next →')}
              </button>
            </div>

            <div style={{ 
              marginTop: isMobile ? 16 : 20, 
              textAlign: 'center', 
              fontSize: isMobile ? 11 : 13, 
              color: '#9ca3af' 
            }}>
              {featureMode === 'topics' && allTopics.length > 0 
                ? `${allTopics.length} topics • ${topicTimerMinutes} ${topicTimerMinutes === 1 ? 'minute' : 'minutes'} per topic`
                : 'Have a great conversation!'
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    height: '100vh',
    width: '100vw',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    margin: 0,
    overflow: 'hidden',
  },
  layoutRow: {
    display: 'flex',
    flexDirection: 'row',
    width: '100vw',
    height: '100vh',
    overflow: 'hidden',
  },
  loadingContainer: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
  },
  chatContainer: {
    flex: 1,
    minWidth: 0,
    height: '100vh',
    backgroundColor: 'white',
    borderRadius: 0,
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  taskSidebar: {
    width: 320,
    maxWidth: 340,
    minWidth: 280,
    height: '100vh',
    background: 'rgba(255,255,255,0.85)',
    backdropFilter: 'blur(2px)',
    borderRight: '1px solid #e5e7eb',
    padding: 16,
    overflowY: 'auto',
    position: 'sticky',
    top: 0,
  },
  mobileSidebarOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    zIndex: 15000,
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'stretch',
    padding: '0 env(safe-area-inset-right, 0px) 0 env(safe-area-inset-left, 0px)',
    overflow: 'hidden'
  },
  mobileSidebarPanel: {
    width: '85%',
    maxWidth: 360,
    height: '100%',
    backgroundColor: '#f9fafb',
    padding: 16,
    paddingTop: 'max(20px, calc(16px + env(safe-area-inset-top, 0px)))',
    paddingBottom: 'max(20px, calc(16px + env(safe-area-inset-bottom, 0px)))',
    boxShadow: '4px 0 24px rgba(0,0,0,0.25)',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    borderTopRightRadius: 20,
    borderBottomRightRadius: 20,
    position: 'relative',
    zIndex: 15001,
    boxSizing: 'border-box',
    maxHeight: '100vh'
  },
  taskCard: {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: 14,
    boxShadow: '0 6px 16px rgba(0,0,0,0.06)'
  },
  taskIcon: {
    width: 28,
    height: 28,
    borderRadius: 8,
    background: '#eef2ff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    width: '90%',
    maxWidth: '480px',
    backgroundColor: 'white',
    borderRadius: 12,
    padding: '20px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
  },
  modalTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 700,
    color: '#111827',
  },
  modalSubtitle: {
    marginTop: 6,
    marginBottom: 12,
    fontSize: '13px',
    color: '#6b7280',
  },
  modalSelect: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    fontSize: '14px',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: 16,
  },
  modalPrimary: {
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    padding: '8px 14px',
    borderRadius: 8,
    cursor: 'pointer',
    fontWeight: 600,
  },
  modalSecondary: {
    backgroundColor: '#f3f4f6',
    color: '#111827',
    border: '1px solid #e5e7eb',
    padding: '8px 14px',
    borderRadius: 8,
    cursor: 'pointer',
    fontWeight: 600,
  },
  header: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    padding: '20px 30px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  backButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    color: 'white',
    border: '1px solid rgba(255,255,255,0.3)',
    padding: '8px 16px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  chatInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
  },
  partnerAvatar: {
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.2)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: 'bold',
    border: '2px solid rgba(255,255,255,0.3)',
  },
  partnerDetails: {
    display: 'flex',
    flexDirection: 'column',
  },
  partnerName: {
    margin: '0 0 5px 0',
    fontSize: '20px',
    fontWeight: '600',
  },
  partnerMeta: {
    margin: 0,
    fontSize: '14px',
    opacity: 0.9,
  },
  connectionStatus: {
    fontWeight: '500',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
  },
  currentUser: {
    fontSize: '14px',
    opacity: 0.8,
  },
  timerChip: {
    fontSize: '13px',
    background: '#fef3c7',
    color: '#92400e',
    border: '1px solid #fde68a',
    padding: '4px 8px',
    borderRadius: 999,
  },
  logoutButton: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    color: 'white',
    border: '1px solid rgba(255,255,255,0.3)',
    padding: '8px 16px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    backgroundColor: '#f8fafc',
    minHeight: 0,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
    color: '#6b7280',
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '20px',
  },
  emptyTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '15px',
  },
  emptyText: {
    fontSize: '16px',
    lineHeight: '1.6',
    maxWidth: '400px',
    margin: '0 auto',
  },
  messageWrapper: {
    display: 'flex',
    maxWidth: '75%',
  },
  message: {
    padding: '15px 20px',
    borderRadius: '20px',
    maxWidth: '100%',
    wordWrap: 'break-word',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  myMessage: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    borderBottomRightRadius: '8px',
  },
  otherMessage: {
    backgroundColor: 'white',
    color: '#1f2937',
    border: '1px solid #e5e7eb',
    borderBottomLeftRadius: '8px',
  },
  senderInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
    fontSize: '12px',
    opacity: 0.8,
  },
  senderName: {
    fontWeight: '600',
  },
  senderLang: {
    fontSize: '11px',
  },
  messageContent: {
    fontSize: '16px',
    lineHeight: '1.4',
    marginBottom: '8px',
  },
  typingIndicatorRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    padding: '8px 0 0',
  },
  typingBubble: {
    padding: '10px 14px',
    borderRadius: 20,
    background: '#eef2ff',
    border: '1px solid rgba(99,102,241,0.2)',
    boxShadow: '0 4px 10px rgba(0,0,0,0.05)',
  },
  typingDotTrack: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 32,
  },
  originalMessage: {
    fontSize: '12px',
    opacity: 0.7,
    fontStyle: 'italic',
    marginBottom: '8px',
    padding: '8px',
    backgroundColor: 'rgba(0,0,0,0.05)',
    borderRadius: '8px',
  },
  messageTime: {
    fontSize: '11px',
    opacity: 0.6,
    textAlign: 'right',
  },
  inputForm: {
    padding: '20px 30px',
    backgroundColor: 'white',
    borderTop: '1px solid #e5e7eb',
  },
  inputContainer: {
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
  },
  messageInput: {
    flex: 1,
    padding: '15px 20px',
    border: '2px solid #e5e7eb',
    borderRadius: '25px',
    fontSize: '16px',
    outline: 'none',
    transition: 'border-color 0.3s ease',
  },
  sendButton: {
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
  },
  sendButtonDisabled: {
    backgroundColor: '#9ca3af',
    cursor: 'not-allowed',
    background: '#9ca3af',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid rgba(255,255,255,0.3)',
    borderTop: '4px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '20px',
  },
  loadingText: {
    margin: 0,
    fontSize: '18px',
  },
  prefPrompt: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: 'rgba(255,255,255,0.2)',
    border: '1px solid rgba(255,255,255,0.3)',
    padding: '6px 10px',
    borderRadius: 8,
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    backdropFilter: 'blur(4px)',
  },
  topicSwitchModal: {
    backgroundColor: 'white',
    borderRadius: 20,
    padding: 40,
    maxWidth: 600,
    width: '90%',
    maxHeight: '85vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    animation: 'slideDown 0.3s ease-out',
  },
};

// Add CSS animation
const styleSheet = document.createElement('style');
styleSheet.type = 'text/css';
styleSheet.innerText = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-30px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  
  .message-input:focus {
    border-color: #667eea !important;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
  }
  
  .send-button:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  }

  @keyframes typingWave {
    0% { transform: translateY(0); opacity: 0.2; }
    30% { transform: translateY(-4px); opacity: 0.9; }
    60% { transform: translateY(0); opacity: 0.4; }
    100% { transform: translateY(0); opacity: 0.2; }
  }

  .typing-dot {
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #6d28d9;
    animation: typingWave 1.2s infinite ease-in-out;
  }

  .typing-dot:nth-child(2) {
    animation-delay: 0.15s;
  }

  .typing-dot:nth-child(3) {
    animation-delay: 0.3s;
  }
`;
styleSheet.onload = null;
try { document.head.appendChild(styleSheet); } catch (e) {}

export default PrivateChat;
